import React, { Fragment, useEffect, useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import swal from 'sweetalert';
import Sidebar from './Sidebar';
import Loader from '../layout/loader';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MonetizationOnOutlinedIcon from '@mui/icons-material/MonetizationOnOutlined';
import CardMembershipOutlinedIcon from '@mui/icons-material/CardMembershipOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import StarRateIcon from '@mui/icons-material/StarRate';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AssignmentReturnIcon from '@mui/icons-material/AssignmentReturn';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import LaunchIcon from '@mui/icons-material/Launch';
import BlockIcon from '@mui/icons-material/Block';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import HistoryIcon from '@mui/icons-material/History';
import SecurityIcon from '@mui/icons-material/Security';

import './vendor360.css';

function Vendor360() {
    const { id } = useParams();
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [activeTab, setActiveTab] = useState('overview');
    const [productFilter, setProductFilter] = useState('all');
    const [productSort, setProductSort] = useState('revenue');
    const [orderStatusFilter, setOrderStatusFilter] = useState('all');
    const [actionLoading, setActionLoading] = useState(false);

    // Bank Account Review Modal States
    const [bankModalOpen, setBankModalOpen] = useState(false);
    const [approveConfirmOpen, setApproveConfirmOpen] = useState(false);
    const [rejectModalOpen, setRejectModalOpen] = useState(false);
    const [rejectReasonType, setRejectReasonType] = useState("Name mismatch");
    const [customRejectReason, setCustomRejectReason] = useState("");
    const [bankActionLoading, setBankActionLoading] = useState(false);

    // Vendor Block/Unblock States
    const [blockVendorOpen, setBlockVendorOpen] = useState(false);
    const [blockReason, setBlockReason] = useState("");
    const [unblockConfirmOpen, setUnblockConfirmOpen] = useState(false);

    // Product Block/Unblock States
    const [productBlockOpen, setProductBlockOpen] = useState(false);
    const [productBlockTarget, setProductBlockTarget] = useState(null);
    const [productBlockReason, setProductBlockReason] = useState("");

    // Review Requests
    const [reviewRequests, setReviewRequests] = useState([]);

    // Messaging States
    const [messageModalOpen, setMessageModalOpen] = useState(false);
    const [messageSubject, setMessageSubject] = useState("");
    const [messageBody, setMessageBody] = useState("");
    const [messageContext, setMessageContext] = useState(null);
    const [conversations, setConversations] = useState([]);
    const [activeConversation, setActiveConversation] = useState(null);
    const [conversationMessages, setConversationMessages] = useState([]);
    const [messageReply, setMessageReply] = useState("");

    const inrFormatter = useMemo(
        () =>
            new Intl.NumberFormat('en-IN', {
                style: 'currency',
                currency: 'INR',
                maximumFractionDigits: 0,
            }),
        []
    );

    const fetchVendorData = async () => {
        try {
            setLoading(true);
            const res = await axios.get(`/api/admin/vendors/${id}/overview`);
            setData(res.data);
        } catch (error) {
            swal({
                title: "Error Loading Vendor 360",
                text: error.response?.data?.msg || "Unable to fetch seller business overview.",
                icon: "error",
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (id) {
            fetchVendorData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const handleVendorStatus = async (newStatus) => {
        const actionLabel = newStatus === 'approved' ? 'Approve' : newStatus === 'suspended' ? 'Suspend' : 'Reject';
        const willProceed = await swal({
            title: `${actionLabel} Seller?`,
            text: `Are you sure you want to ${actionLabel.toLowerCase()} this seller account?`,
            icon: "warning",
            buttons: ["Cancel", `Yes, ${actionLabel}`],
            dangerMode: newStatus === 'suspended' || newStatus === 'rejected',
        });

        if (!willProceed) return;

        try {
            setActionLoading(true);
            await axios.put(`/api/admin/vendor/${id}/status`, { status: newStatus });
            swal({
                title: "Updated",
                text: `Seller status has been updated to ${newStatus}.`,
                icon: "success"
            });
            fetchVendorData();
        } catch (error) {
            swal({
                title: "Action Failed",
                text: error.response?.data?.msg || "Could not update seller status.",
                icon: "error"
            });
        } finally {
            setActionLoading(false);
        }
    };

    const handleProductApproval = async (productId, status) => {
        let rejectionReason = '';
        if (status === 'rejected') {
            rejectionReason = prompt("Please provide a reason for rejecting this product listing:") || '';
            if (rejectionReason.trim() === '') {
                swal({ title: "Rejection Cancelled", text: "A rejection reason is required for seller feedback.", icon: "info" });
                return;
            }
        }

        try {
            setActionLoading(true);
            await axios.put(`/api/admin/vendor/product/${productId}/status`, {
                approvalStatus: status,
                rejectionReason
            });
            swal({
                title: "Product Updated",
                text: `Product has been ${status === 'approved' ? 'approved' : 'rejected'}.`,
                icon: "success"
            });
            fetchVendorData();
        } catch (error) {
            swal({
                title: "Failed",
                text: error.response?.data?.msg || "Could not update product status.",
                icon: "error"
            });
        } finally {
            setActionLoading(false);
        }
    };

    const handleVerifyDocument = async (docType, status) => {
        let rejectionReason = '';
        if (status === 'rejected' || status === 'requires_reupload') {
            rejectionReason = prompt(`Please enter the compliance reason for ${status === 'rejected' ? 'rejecting' : 'requesting re-upload of'} ${docType.toUpperCase()}:`) || '';
            if (!rejectionReason.trim()) {
                swal({ title: "Action Cancelled", text: "Feedback note is required for the seller.", icon: "info" });
                return;
            }
        }

        try {
            setActionLoading(true);
            await axios.put(`/api/admin/vendor/${id}/document/${docType}/status`, {
                status,
                rejectionReason
            });
            swal({
                title: "Document Updated",
                text: `${docType.toUpperCase()} marked as ${status}.`,
                icon: "success"
            });
            fetchVendorData();
        } catch (error) {
            swal({
                title: "Action Failed",
                text: error.response?.data?.msg || "Could not update document status.",
                icon: "error"
            });
        } finally {
            setActionLoading(false);
        }
    };

    const handleApproveBank = async () => {
        try {
            setBankActionLoading(true);
            await axios.put(`/api/admin/vendor/${id}/bank/status`, {
                status: "verified"
            });
            swal({
                title: "Bank Account Approved",
                text: `Settlement bank account for ${data?.vendor?.businessName || 'this vendor'} has been approved and activated.`,
                icon: "success"
            });
            setApproveConfirmOpen(false);
            setBankModalOpen(false);
            fetchVendorData();
        } catch (error) {
            swal({
                title: "Approval Failed",
                text: error.response?.data?.msg || "Unable to approve bank account. Please try again.",
                icon: "error"
            });
        } finally {
            setBankActionLoading(false);
        }
    };

    const handleRejectBank = async (e) => {
        if (e) e.preventDefault();
        const finalReason = rejectReasonType === "Other"
            ? customRejectReason.trim()
            : rejectReasonType + (customRejectReason.trim() ? `: ${customRejectReason.trim()}` : "");

        if (!finalReason) {
            swal({ title: "Reason Required", text: "Please provide a reason for rejecting the bank account.", icon: "info" });
            return;
        }

        try {
            setBankActionLoading(true);
            await axios.put(`/api/admin/vendor/${id}/bank/status`, {
                status: "rejected",
                rejectionReason: finalReason
            });
            swal({
                title: "Bank Account Rejected",
                text: `Bank details marked as rejected. Seller has been notified with the reason.`,
                icon: "warning"
            });
            setRejectModalOpen(false);
            setBankModalOpen(false);
            setCustomRejectReason("");
            fetchVendorData();
        } catch (error) {
            swal({
                title: "Rejection Failed",
                text: error.response?.data?.msg || "Could not reject bank account. Please try again.",
                icon: "error"
            });
        } finally {
            setBankActionLoading(false);
        }
    };

    const handleToggleListing = async (currentStatus) => {
        const newStatus = !currentStatus;
        try {
            setActionLoading(true);
            await axios.put(`/api/admin/vendor/${id}/listing-toggle`, {
                isListingEnabled: newStatus
            });
            swal({
                title: "Listing Permission Updated",
                text: `Product listing has been ${newStatus ? 'enabled' : 'locked'} for this seller.`,
                icon: "success"
            });
            fetchVendorData();
        } catch (error) {
            swal({
                title: "Action Failed",
                text: error.response?.data?.msg || "Could not toggle product listing.",
                icon: "error"
            });
        } finally {
            setActionLoading(false);
        }
    };

    // ══════════ BLOCK VENDOR ══════════
    const handleBlockVendor = async (e) => {
        if (e) e.preventDefault();
        if (!blockReason.trim()) {
            swal({ title: "Reason Required", text: "Please provide a reason for blocking this vendor.", icon: "info" });
            return;
        }
        try {
            setActionLoading(true);
            await axios.put(`/api/admin/vendor/${id}/status`, { status: "blocked", reason: blockReason.trim() });
            swal({ title: "Vendor Blocked", text: "This vendor has been blocked. All their products are now hidden from the marketplace.", icon: "warning" });
            setBlockVendorOpen(false);
            setBlockReason("");
            fetchVendorData();
        } catch (error) {
            swal({ title: "Action Failed", text: error.response?.data?.msg || "Could not block vendor.", icon: "error" });
        } finally {
            setActionLoading(false);
        }
    };

    const handleUnblockVendor = async () => {
        try {
            setActionLoading(true);
            await axios.put(`/api/admin/vendor/${id}/status`, { status: "approved" });
            swal({ title: "Vendor Unblocked", text: "Vendor has been restored. Product visibility depends on their listing permission.", icon: "success" });
            setUnblockConfirmOpen(false);
            fetchVendorData();
        } catch (error) {
            swal({ title: "Action Failed", text: error.response?.data?.msg || "Could not unblock vendor.", icon: "error" });
        } finally {
            setActionLoading(false);
        }
    };

    // ══════════ BLOCK / UNBLOCK PRODUCT ══════════
    const handleBlockProduct = async (e) => {
        if (e) e.preventDefault();
        if (!productBlockReason.trim()) {
            swal({ title: "Reason Required", text: "Please provide a reason for blocking this product listing.", icon: "info" });
            return;
        }
        try {
            setActionLoading(true);
            await axios.put(`/api/admin/vendor/product/${productBlockTarget._id}/block`, { reason: productBlockReason.trim() });
            swal({ title: "Product Blocked", text: "This product has been removed from the marketplace.", icon: "warning" });
            setProductBlockOpen(false);
            setProductBlockTarget(null);
            setProductBlockReason("");
            fetchVendorData();
        } catch (error) {
            swal({ title: "Action Failed", text: error.response?.data?.msg || "Could not block product.", icon: "error" });
        } finally {
            setActionLoading(false);
        }
    };

    const handleUnblockProduct = async (productId) => {
        const willProceed = await swal({
            title: "Unblock Product?",
            text: "This will restore the product to the marketplace if the vendor is active.",
            icon: "warning",
            buttons: ["Cancel", "Yes, Unblock"],
        });
        if (!willProceed) return;
        try {
            setActionLoading(true);
            await axios.put(`/api/admin/vendor/product/${productId}/unblock`);
            swal({ title: "Product Unblocked", text: "Product listing has been restored.", icon: "success" });
            fetchVendorData();
        } catch (error) {
            swal({ title: "Action Failed", text: error.response?.data?.msg || "Could not unblock product.", icon: "error" });
        } finally {
            setActionLoading(false);
        }
    };

    // ══════════ MESSAGING ══════════
    const handleSendMessage = async (e) => {
        if (e) e.preventDefault();
        if (!messageBody.trim()) {
            swal({ title: "Message Required", text: "Please enter a message.", icon: "info" });
            return;
        }
        try {
            setActionLoading(true);
            await axios.post(`/api/admin/messages/send`, {
                vendorId: data?.vendor?.userId || id,
                subject: messageSubject.trim(),
                message: messageBody.trim(),
                context: messageContext || undefined
            });
            swal({ title: "Message Sent", text: "Your message has been sent to the vendor.", icon: "success" });
            setMessageModalOpen(false);
            setMessageSubject("");
            setMessageBody("");
            setMessageContext(null);
            // Refresh conversations if on messages tab
            if (activeTab === 'messages') fetchConversations();
        } catch (error) {
            swal({ title: "Send Failed", text: error.response?.data?.msg || "Could not send message.", icon: "error" });
        } finally {
            setActionLoading(false);
        }
    };

    const fetchConversations = async () => {
        try {
            const res = await axios.get(`/api/admin/messages/conversations`);
            setConversations(res.data?.conversations || []);
        } catch (err) { console.warn("Could not load conversations:", err.message); }
    };

    const fetchConversationMessages = async (convId) => {
        try {
            const res = await axios.get(`/api/admin/messages/${convId}`);
            setConversationMessages(res.data?.messages || []);
            // Mark as read
            await axios.put(`/api/admin/messages/${convId}/read`).catch(() => {});
        } catch (err) { console.warn("Could not load messages:", err.message); }
    };

    const handleSendReply = async (convId) => {
        if (!messageReply.trim()) return;
        try {
            await axios.post(`/api/admin/messages/send`, {
                vendorId: data?.vendor?.userId || id,
                conversationId: convId,
                message: messageReply.trim()
            });
            setMessageReply("");
            fetchConversationMessages(convId);
        } catch (error) {
            swal({ title: "Send Failed", text: error.response?.data?.msg || "Could not send reply.", icon: "error" });
        }
    };

    // ══════════ REVIEW REQUESTS ══════════
    const fetchReviewRequests = async () => {
        try {
            const res = await axios.get(`/api/admin/review-requests`);
            setReviewRequests(res.data?.requests || []);
        } catch (err) { console.warn("Could not load review requests:", err.message); }
    };

    const handleResolveReview = async (requestId, action, adminResponse) => {
        try {
            setActionLoading(true);
            await axios.put(`/api/admin/review-requests/${requestId}`, { action, adminResponse });
            swal({
                title: action === "approved" ? "Review Approved" : "Review Rejected",
                text: action === "approved" ? "Product has been restored to the marketplace." : "Product remains blocked.",
                icon: action === "approved" ? "success" : "info"
            });
            fetchReviewRequests();
            fetchVendorData();
        } catch (error) {
            swal({ title: "Action Failed", text: error.response?.data?.msg || "Could not process review.", icon: "error" });
        } finally {
            setActionLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="dashboard-container">
                <Sidebar />
                <main className="main-container v360-main">
                    <Loader />
                </main>
            </div>
        );
    }

    if (!data || !data.vendor) {
        return (
            <div className="dashboard-container">
                <Sidebar />
                <main className="main-container v360-main" style={{ textAlign: 'center', padding: '60px 20px' }}>
                    <h2>Seller Not Found</h2>
                    <p style={{ color: '#6a6864' }}>We couldn't load this seller's 360 overview.</p>
                    <Link to="/Admin/dashboard/Vendors" className="v360-btn-primary" style={{ margin: '14px auto', width: 'fit-content' }}>
                        Back to Sellers
                    </Link>
                </main>
            </div>
        );
    }

    const {
        vendor,
        store,
        plan,
        kpis,
        capacity,
        bestSeller,
        topProducts = [],
        allProducts = [],
        pendingApprovalQueue = [],
        orders = [],
        reviews = { ratingDistribution: {}, recentReviews: [] },
        returns = { list: [] },
        criticalAlerts = [],
        healthCheckpoints = [],
        growthOpportunities = [],
        activityTimeline = [],
        channelBreakdown = {},
        omnichannelSummary = {},
        customerGeographicInflow = {}
    } = data;

    // Ensure bankDetails is always available on vendor
    if (vendor) {
        vendor.bankDetails = vendor.bankDetails || data.bankDetails || {};
    }

    // Filter and sort products for the Products Tab
    const filteredProducts = allProducts.filter(p => {
        if (productFilter === 'all') return true;
        if (productFilter === 'pending') return p.approvalStatus === 'pending';
        if (productFilter === 'approved') return p.approvalStatus === 'approved';
        if (productFilter === 'rejected') return p.approvalStatus === 'rejected';
        if (productFilter === 'low_stock') return p.productStock <= 5 && p.productStock > 0;
        if (productFilter === 'out_of_stock') return p.productStock === 0;
        return true;
    }).sort((a, b) => {
        if (productSort === 'revenue') return b.revenue - a.revenue;
        if (productSort === 'orders') return b.ordersCount - a.ordersCount;
        if (productSort === 'units') return b.unitsSold - a.unitsSold;
        if (productSort === 'rating') return (b.ratings || 0) - (a.ratings || 0);
        return 0;
    });

    // Filter orders for the Orders Tab
    const filteredOrders = orders.filter(o => {
        if (orderStatusFilter === 'all') return true;
        return o.orderStatus === orderStatusFilter;
    });

    return (
        <Fragment>
            <div className="dashboard-container">
                <Sidebar />
                <main className="main-container v360-main">
                    {/* Back link */}
                    <Link to="/Admin/dashboard/Vendors" className="v360-back-link">
                        <ArrowBackIcon style={{ fontSize: '16px' }} />
                        <span>Back to All Sellers</span>
                    </Link>

                    {/* ═══════════════════════ 1. VENDOR PROFILE HEADER ═══════════════════════ */}
                    <header className="v360-header-card" style={{ border: '1px solid #e2e8f0', borderRadius: '16px', padding: '24px', background: '#ffffff', boxShadow: '0 4px 20px rgba(0,0,0,0.03)' }}>
                        <div className="v360-header-top" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '20px', flexWrap: 'wrap' }}>
                            {/* Left: Seller Identity */}
                            <div className="v360-seller-profile-group" style={{ display: 'flex', alignItems: 'center', gap: '18px' }}>
                                <div className="v360-seller-avatar" style={{ width: '64px', height: '64px', borderRadius: '14px', background: 'linear-gradient(135deg, #faedea 0%, #fee2e2 100%)', color: '#a85050', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px', fontWeight: 900, border: '2px solid #fecaca', boxShadow: '0 2px 8px rgba(168,80,80,0.1)' }}>
                                    {store?.logoUrl ? (
                                        <img src={store.logoUrl} alt={store.name} style={{ width: '100%', height: '100%', borderRadius: '12px', objectFit: 'cover' }} />
                                    ) : (
                                        <span>{(store?.name || vendor.businessName || 'S').charAt(0).toUpperCase()}</span>
                                    )}
                                </div>
                                <div className="v360-seller-names">
                                    <div className="v360-store-name-row" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <h1 className="v360-store-title" style={{ fontSize: '22px', fontWeight: 900, color: '#0f172a', margin: 0, letterSpacing: '-0.3px' }}>
                                            {store?.name || vendor.businessName}
                                        </h1>
                                        {vendor.status === 'approved' && (
                                            <VerifiedUserIcon style={{ fontSize: '20px', color: '#16a34a' }} titleAccess="Verified Seller" />
                                        )}
                                    </div>
                                    <div className="v360-seller-name-sub" style={{ fontSize: '13.5px', color: '#475569', marginTop: '2px', fontWeight: 600 }}>
                                        {vendor.sellerName} &middot; <span style={{ textTransform: 'capitalize', color: '#64748b' }}>{vendor.businessType || 'Individual'}</span>
                                    </div>
                                    <div className="v360-seller-contact-row" style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '8px', flexWrap: 'wrap' }}>
                                        {vendor.email && (
                                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '12px', background: '#f8fafc', padding: '4px 10px', borderRadius: '6px', border: '1px solid #e2e8f0', color: '#475569' }}>
                                                <EmailIcon style={{ fontSize: '14px', color: '#64748b' }} /> {vendor.email}
                                            </span>
                                        )}
                                        {vendor.phone && (
                                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '12px', background: '#f8fafc', padding: '4px 10px', borderRadius: '6px', border: '1px solid #e2e8f0', color: '#475569' }}>
                                                <PhoneIcon style={{ fontSize: '14px', color: '#64748b' }} /> {vendor.phone}
                                            </span>
                                        )}
                                        {(vendor.address?.city || vendor.address?.state) && (
                                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '12px', background: '#f8fafc', padding: '4px 10px', borderRadius: '6px', border: '1px solid #e2e8f0', color: '#475569' }}>
                                                <LocationOnIcon style={{ fontSize: '14px', color: '#64748b' }} /> {vendor.address?.city || vendor.address?.state}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Right: Actions Bar */}
                            <div className="v360-header-actions" style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                <button
                                    type="button"
                                    onClick={fetchVendorData}
                                    disabled={actionLoading}
                                    style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '8px 14px', fontSize: '13px', fontWeight: 700, borderRadius: '8px', background: '#ffffff', border: '1.5px solid #e2e8f0', color: '#334155', cursor: 'pointer' }}
                                >
                                    <RefreshIcon style={{ fontSize: '16px' }} /> Refresh
                                </button>
                                
                                {store?.slug && (
                                    <a
                                        href={`https://www.imonline.shop/store/${store.slug}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '8px 14px', fontSize: '13px', fontWeight: 700, borderRadius: '8px', background: '#ffffff', border: '1.5px solid #e2e8f0', color: '#334155', textDecoration: 'none' }}
                                    >
                                        <LaunchIcon style={{ fontSize: '15px' }} /> View Store
                                    </a>
                                )}

                                {/* Message Vendor */}
                                <button
                                    type="button"
                                    onClick={() => setMessageModalOpen(true)}
                                    disabled={actionLoading}
                                    style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '8px 16px', fontSize: '13px', fontWeight: 700, borderRadius: '8px', background: '#eff6ff', border: '1.5px solid #bfdbfe', color: '#1d4ed8', cursor: 'pointer' }}
                                >
                                    <span>✉</span> Message Vendor
                                </button>

                                {/* Lifecycle & Governance Buttons */}
                                {vendor.status === 'pending' && (
                                    <button
                                        type="button"
                                        onClick={() => handleVendorStatus('approved')}
                                        disabled={actionLoading}
                                        style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '8px 16px', fontSize: '13px', fontWeight: 700, borderRadius: '8px', background: '#f0fdf4', border: '1.5px solid #bbf7d0', color: '#15803d', cursor: 'pointer' }}
                                    >
                                        <CheckIcon style={{ fontSize: '16px' }} /> Approve Seller
                                    </button>
                                )}

                                {vendor.status === 'approved' && (
                                    <button
                                        type="button"
                                        onClick={() => handleVendorStatus('suspended')}
                                        disabled={actionLoading}
                                        style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '8px 14px', fontSize: '13px', fontWeight: 700, borderRadius: '8px', background: '#fffbeb', border: '1.5px solid #fde68a', color: '#b45309', cursor: 'pointer' }}
                                    >
                                        <BlockIcon style={{ fontSize: '16px' }} /> Suspend
                                    </button>
                                )}

                                {vendor.status === 'suspended' && (
                                    <button
                                        type="button"
                                        onClick={() => handleVendorStatus('approved')}
                                        disabled={actionLoading}
                                        style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '8px 16px', fontSize: '13px', fontWeight: 700, borderRadius: '8px', background: '#f0fdf4', border: '1.5px solid #bbf7d0', color: '#15803d', cursor: 'pointer' }}
                                    >
                                        <CheckIcon style={{ fontSize: '16px' }} /> Reactivate
                                    </button>
                                )}

                                {vendor.status !== 'blocked' && vendor.status !== 'pending' && (
                                    <button
                                        type="button"
                                        onClick={() => setBlockVendorOpen(true)}
                                        disabled={actionLoading}
                                        style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '8px 14px', fontSize: '13px', fontWeight: 700, borderRadius: '8px', background: '#fef2f2', border: '1.5px solid #fecaca', color: '#dc2626', cursor: 'pointer' }}
                                    >
                                        <BlockIcon style={{ fontSize: '16px' }} /> Block Vendor
                                    </button>
                                )}

                                {vendor.status === 'blocked' && (
                                    <button
                                        type="button"
                                        onClick={() => setUnblockConfirmOpen(true)}
                                        disabled={actionLoading}
                                        style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '8px 16px', fontSize: '13px', fontWeight: 700, borderRadius: '8px', background: '#f0fdf4', border: '1.5px solid #bbf7d0', color: '#15803d', cursor: 'pointer' }}
                                    >
                                        <CheckIcon style={{ fontSize: '16px' }} /> Unblock Vendor
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* Bottom Metric & Status Strip */}
                        <div className="v360-header-pill-strip" style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '18px', paddingTop: '16px', borderTop: '1px solid #f1f5f9', flexWrap: 'wrap' }}>
                            <span style={{
                                fontSize: '11.5px',
                                fontWeight: 900,
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px',
                                padding: '4px 12px',
                                borderRadius: '99px',
                                background: vendor.status === 'approved' ? '#dcfce7' : vendor.status === 'blocked' ? '#fee2e2' : vendor.status === 'suspended' ? '#fef3c7' : '#f1f5f9',
                                color: vendor.status === 'approved' ? '#166534' : vendor.status === 'blocked' ? '#991b1b' : vendor.status === 'suspended' ? '#92400e' : '#475569',
                                border: `1px solid ${vendor.status === 'approved' ? '#bbf7d0' : vendor.status === 'blocked' ? '#fecaca' : vendor.status === 'suspended' ? '#fde68a' : '#e2e8f0'}`
                            }}>
                                ● {vendor.status || 'Pending'}
                            </span>

                            <span style={{ fontSize: '12px', fontWeight: 700, background: '#f8fafc', color: '#334155', padding: '4px 12px', borderRadius: '99px', border: '1px solid #e2e8f0' }}>
                                🏷 {plan.name} Plan &middot; {plan.commissionRate}% Commission
                            </span>

                            <span style={{ fontSize: '12px', fontWeight: 700, background: '#f8fafc', color: '#334155', padding: '4px 12px', borderRadius: '99px', border: '1px solid #e2e8f0' }}>
                                📦 {capacity.productsUsed} / {capacity.productLimit} Products ({capacity.percentage}%)
                            </span>

                            {vendor.joinedAt && (
                                <span style={{ fontSize: '11.5px', color: '#94a3b8', marginLeft: 'auto' }}>
                                    Joined on {new Date(vendor.joinedAt).toLocaleDateString('en-IN', { month: 'short', day: 'numeric', year: 'numeric' })}
                                </span>
                            )}
                        </div>
                    </header>

                    {/* ═══════════════════════ 2. NAVIGATION TABS ═══════════════════════ */}
                    <nav className="v360-tabs-nav" aria-label="Vendor 360 Tabs">
                        {[
                            { id: 'overview', label: 'Overview' },
                            { id: 'products', label: 'Products', count: allProducts.length },
                            { id: 'orders', label: 'Orders', count: orders.length },
                            { id: 'sales', label: 'Sales & Commission' },
                            { id: 'reviews', label: 'Reviews', count: kpis.totalReviews },
                            { id: 'returns', label: 'Returns', count: returns.returnsCount },
                            { id: 'store', label: 'Store Profile' },
                            { id: 'plan', label: 'Plan & Billing' },
                            { id: 'verification', label: 'Verification' },
                            { id: 'activity', label: 'Activity' },
                            { id: 'messages', label: 'Messages' },
                            { id: 'review-requests', label: 'Review Requests', count: reviewRequests.filter(r => r.status === 'pending').length },
                        ].map((t) => (
                            <button
                                key={t.id}
                                type="button"
                                className={`v360-tab-btn ${activeTab === t.id ? 'active' : ''}`}
                                onClick={() => setActiveTab(t.id)}
                            >
                                <span>{t.label}</span>
                                {t.count !== undefined && t.count > 0 && (
                                    <span className="v360-tab-count-badge">{t.count}</span>
                                )}
                            </button>
                        ))}
                    </nav>

                    {/* ═══════════════════════════════════════════════════════════════════ */}
                    {/* TAB 1: OVERVIEW (THE 30-SECOND DECISION-MAKING CENTER)              */}
                    {/* ═══════════════════════════════════════════════════════════════════ */}
                    {activeTab === 'overview' && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                            {/* Critical Alerts Banner Strip */}
                            {criticalAlerts.length > 0 && (
                                <div className="v360-alerts-container">
                                    {criticalAlerts.map((alert, idx) => (
                                        <div key={idx} className={`v360-alert-bar ${alert.type}`}>
                                            <div className="v360-alert-left">
                                                {alert.type === 'danger' && <ErrorOutlineIcon style={{ fontSize: '20px' }} />}
                                                {alert.type === 'warning' && <WarningAmberOutlinedIcon style={{ fontSize: '20px' }} />}
                                                {alert.type === 'info' && <InfoOutlinedIcon style={{ fontSize: '20px' }} />}
                                                {alert.type === 'success' && <CheckCircleOutlineIcon style={{ fontSize: '20px' }} />}
                                                <div>
                                                    <span className="v360-alert-title">{alert.title}</span>
                                                    <span className="v360-alert-desc"> — {alert.desc}</span>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* Seller Health & Onboarding Status */}
                            <div className="v360-panel" style={{ padding: '20px 24px', background: '#fbfaf8', border: '1px solid #edf0f4', borderRadius: '16px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' }}>
                                    <div>
                                        <h3 className="v360-panel-title" style={{ fontSize: '16px', fontWeight: 800, color: '#0f172a', margin: 0 }}>Seller Health &amp; Onboarding Status</h3>
                                        <span className="v360-panel-sub" style={{ fontSize: '12.5px', color: '#64748b' }}>Operational readiness across compliance, bank payouts &amp; catalogue publishing</span>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        <div style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '6px',
                                            fontSize: '12px',
                                            fontWeight: 800,
                                            padding: '6px 14px',
                                            borderRadius: '20px',
                                            background: data?.sellerHealth?.listingEnabled ? '#dcfce7' : '#fee2e2',
                                            color: data?.sellerHealth?.listingEnabled ? '#166534' : '#991b1b',
                                            border: `1px solid ${data?.sellerHealth?.listingEnabled ? '#bbf7d0' : '#fecaca'}`
                                        }}>
                                            <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: data?.sellerHealth?.listingEnabled ? '#16a34a' : '#dc2626' }} />
                                            Listing: {data?.sellerHealth?.listingEnabled ? 'ON' : 'OFF'}
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => handleToggleListing(data?.sellerHealth?.listingEnabled)}
                                            className="btn btn-sm"
                                            disabled={actionLoading}
                                            style={{
                                                fontSize: '12.5px',
                                                padding: '7px 16px',
                                                borderRadius: '8px',
                                                fontWeight: 800,
                                                cursor: 'pointer',
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: '6px',
                                                background: data?.sellerHealth?.listingEnabled ? '#fee2e2' : '#dcfce7',
                                                color: data?.sellerHealth?.listingEnabled ? '#991b1b' : '#166534',
                                                border: `1.5px solid ${data?.sellerHealth?.listingEnabled ? '#fca5a5' : '#86efac'}`
                                            }}
                                        >
                                            {data?.sellerHealth?.listingEnabled ? '🔒 Lock / Turn OFF Listing' : '🔓 Enable / Turn ON Listing'}
                                        </button>
                                    </div>
                                </div>

                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '14px' }}>
                                    <div style={{ background: '#ffffff', border: '1px solid #edf0f4', borderRadius: '10px', padding: '14px' }}>
                                        <span style={{ fontSize: '11px', color: '#737a84', fontWeight: 800, textTransform: 'uppercase' }}>Application</span>
                                        <div style={{ fontSize: '14px', fontWeight: 800, color: '#166534', marginTop: '2px' }}>
                                            ✓ {vendor.status?.toUpperCase()}
                                        </div>
                                        <span style={{ fontSize: '11px', color: '#64748b', marginTop: '2px', display: 'block' }}>Account registered</span>
                                    </div>

                                    <div style={{ background: '#ffffff', border: '1px solid #edf0f4', borderRadius: '10px', padding: '14px' }}>
                                        <span style={{ fontSize: '11px', color: '#737a84', fontWeight: 800, textTransform: 'uppercase' }}>Documents</span>
                                        <div style={{ fontSize: '14px', fontWeight: 800, color: data?.sellerHealth?.documentsVerified ? '#166534' : '#92400e', marginTop: '2px' }}>
                                            {data?.sellerHealth?.documentsVerifiedText || '2 / 2 Verified'}
                                        </div>
                                        <span style={{ fontSize: '11px', color: '#64748b', marginTop: '2px', display: 'block' }}>KYC credentials</span>
                                    </div>

                                    <div style={{ background: '#ffffff', border: '1px solid #edf0f4', borderRadius: '10px', padding: '14px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                                        <div>
                                            <span style={{ fontSize: '11px', color: '#737a84', fontWeight: 800, textTransform: 'uppercase' }}>Bank Account</span>
                                            <div style={{ fontSize: '14px', fontWeight: 800, color: data?.sellerHealth?.bankVerified ? '#166534' : data?.sellerHealth?.bankStatus === 'rejected' ? '#991b1b' : '#92400e', marginTop: '2px', textTransform: 'capitalize' }}>
                                                {data?.sellerHealth?.bankStatus === 'verified' ? '✓ Verified' : data?.sellerHealth?.bankStatus === 'under_review' ? '● Under Review' : data?.sellerHealth?.bankStatus === 'rejected' ? '✕ Rejected' : (data?.sellerHealth?.bankStatus || 'Pending')}
                                            </div>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => setBankModalOpen(true)}
                                            className="btn btn-sm"
                                            style={{
                                                marginTop: '8px',
                                                fontSize: '11.5px',
                                                fontWeight: 800,
                                                padding: '5px 10px',
                                                background: data?.sellerHealth?.bankVerified ? '#f0fdf4' : '#eff6ff',
                                                color: data?.sellerHealth?.bankVerified ? '#166534' : '#1e40af',
                                                border: `1px solid ${data?.sellerHealth?.bankVerified ? '#bbf7d0' : '#bfdbfe'}`,
                                                borderRadius: '6px',
                                                cursor: 'pointer'
                                            }}
                                        >
                                            {data?.sellerHealth?.bankVerified ? 'View Bank Details' : 'Review Bank Account'}
                                        </button>
                                    </div>

                                    <div style={{ background: '#ffffff', border: '1px solid #edf0f4', borderRadius: '10px', padding: '14px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                                        <div>
                                            <span style={{ fontSize: '11px', color: '#737a84', fontWeight: 800, textTransform: 'uppercase' }}>Product Listing</span>
                                            <div style={{
                                                fontSize: '14px',
                                                fontWeight: 900,
                                                color: data?.sellerHealth?.listingEnabled ? '#166534' : '#991b1b',
                                                marginTop: '2px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '6px'
                                            }}>
                                                <span>{data?.sellerHealth?.listingEnabled ? '🟢 Enabled (ON)' : '🔒 Locked (OFF)'}</span>
                                            </div>
                                            <span style={{ fontSize: '11px', color: '#64748b', marginTop: '2px', display: 'block' }}>
                                                {data?.sellerHealth?.listingEnabled ? 'Accepting new listings' : 'Listing locked by policy'}
                                            </span>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => handleToggleListing(data?.sellerHealth?.listingEnabled)}
                                            disabled={actionLoading}
                                            style={{
                                                marginTop: '8px',
                                                fontSize: '11.5px',
                                                fontWeight: 800,
                                                padding: '5px 12px',
                                                borderRadius: '6px',
                                                cursor: 'pointer',
                                                width: '100%',
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                gap: '5px',
                                                background: data?.sellerHealth?.listingEnabled ? '#fef2f2' : '#f0fdf4',
                                                color: data?.sellerHealth?.listingEnabled ? '#dc2626' : '#16a34a',
                                                border: `1px solid ${data?.sellerHealth?.listingEnabled ? '#fecaca' : '#bbf7d0'}`
                                            }}
                                        >
                                            {data?.sellerHealth?.listingEnabled ? '⏻ Turn OFF Listing' : '⏻ Turn ON Listing'}
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {/* Business Overview KPIs */}
                            <section className="v360-kpi-grid">
                                <div className="v360-kpi-card">
                                    <div className="v360-kpi-header">
                                        <span className="v360-kpi-label">Gross Sales</span>
                                        <MonetizationOnOutlinedIcon className="v360-kpi-icon" />
                                    </div>
                                    <div className="v360-kpi-val highlight-brand">{inrFormatter.format(kpis.grossSales)}</div>
                                    <span className="v360-kpi-meta">{kpis.totalOrders} total orders</span>
                                </div>

                                <div className="v360-kpi-card">
                                    <div className="v360-kpi-header">
                                        <span className="v360-kpi-label">Marketplace Commission</span>
                                        <CardMembershipOutlinedIcon className="v360-kpi-icon" />
                                    </div>
                                    <div className="v360-kpi-val">{inrFormatter.format(kpis.totalCommission)}</div>
                                    <span className="v360-kpi-meta">{plan.commissionRate}% plan tier rate</span>
                                </div>

                                <div className="v360-kpi-card">
                                    <div className="v360-kpi-header">
                                        <span className="v360-kpi-label">Seller Earnings</span>
                                        <TrendingUpIcon className="v360-kpi-icon" />
                                    </div>
                                    <div className="v360-kpi-val highlight-green">{inrFormatter.format(kpis.sellerEarnings)}</div>
                                    <span className="v360-kpi-meta">Net payout due</span>
                                </div>

                                <div className="v360-kpi-card">
                                    <div className="v360-kpi-header">
                                        <span className="v360-kpi-label">Product Capacity</span>
                                        <Inventory2OutlinedIcon className="v360-kpi-icon" />
                                    </div>
                                    <div className="v360-kpi-val">{capacity.productsUsed} / {capacity.productLimit}</div>
                                    <span className="v360-kpi-meta">{kpis.pendingProducts} awaiting approval</span>
                                </div>

                                <div className="v360-kpi-card">
                                    <div className="v360-kpi-header">
                                        <span className="v360-kpi-label">Average Rating</span>
                                        <StarRateIcon className="v360-kpi-icon" style={{ color: '#d97706' }} />
                                    </div>
                                    <div className="v360-kpi-val highlight-amber">{kpis.averageRating > 0 ? `${kpis.averageRating} ★` : '—'}</div>
                                    <span className="v360-kpi-meta">{kpis.totalReviews} total reviews</span>
                                </div>

                                <div className="v360-kpi-card">
                                    <div className="v360-kpi-header">
                                        <span className="v360-kpi-label">Return &amp; Cancel</span>
                                        <AssignmentReturnIcon className="v360-kpi-icon" />
                                    </div>
                                    <div className="v360-kpi-val">{kpis.returnRate}% / {kpis.cancellationRate}%</div>
                                    <span className="v360-kpi-meta">{returns.returnsCount} returns recorded</span>
                                </div>
                            </section>

                            {/* ═══════════════════════ OMNICHANNEL & CUSTOMER INFLOW (ADMIN GOVERNANCE) ═══════════════════════ */}
                            <div className="v360-panel" style={{ marginBottom: '24px', background: '#ffffff', borderRadius: '14px', border: '1px solid #e2e8f0', padding: '22px 24px', boxShadow: '0 2px 10px rgba(0,0,0,0.02)' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '18px', flexWrap: 'wrap', gap: '10px' }}>
                                    <div>
                                        <span style={{ fontSize: '11px', fontWeight: 800, color: '#2563eb', textTransform: 'uppercase', letterSpacing: '0.8px' }}>
                                            OMNICHANNEL FULFILLMENT &amp; DEMAND PATTERNS
                                        </span>
                                        <h3 style={{ fontSize: '18px', fontWeight: 900, color: '#0f172a', margin: '2px 0 0' }}>
                                            Order Fulfillment Modes &amp; Customer Geographic Inflow
                                        </h3>
                                    </div>
                                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                        <span style={{ fontSize: '12px', background: '#f0fdf4', color: '#166534', padding: '4px 10px', borderRadius: '99px', fontWeight: 700, border: '1px solid #bbf7d0' }}>
                                            🚚 Online Delivery: {omnichannelSummary.onlineOrders || 0}
                                        </span>
                                        <span style={{ fontSize: '12px', background: '#eff6ff', color: '#1e40af', padding: '4px 10px', borderRadius: '99px', fontWeight: 700, border: '1px solid #bfdbfe' }}>
                                            🏪 Store Pickups: {omnichannelSummary.inStorePickups || 0}
                                        </span>
                                        <span style={{ fontSize: '12px', background: '#fef3c7', color: '#92400e', padding: '4px 10px', borderRadius: '99px', fontWeight: 700, border: '1px solid #fde68a' }}>
                                            🔖 Token Reserves: {omnichannelSummary.advanceReservations || 0}
                                        </span>
                                        <span style={{ fontSize: '12px', background: '#f3e8ff', color: '#7e22ce', padding: '4px 10px', borderRadius: '99px', fontWeight: 700, border: '1px solid #e9d5ff' }}>
                                            📌 24h Hold Bookings: {omnichannelSummary.holdBookings || 0}
                                        </span>
                                        <span style={{ fontSize: '12px', background: '#ffedd5', color: '#c2410c', padding: '4px 10px', borderRadius: '99px', fontWeight: 700, border: '1px solid #fed7aa' }}>
                                            📞 Direct Calls: {omnichannelSummary.callLeads || 0}
                                        </span>
                                    </div>
                                </div>

                                {/* Channel Distribution Row */}
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px', marginBottom: '20px' }}>
                                    <div style={{ background: '#f8fafc', border: '1px solid #edf0f4', borderRadius: '10px', padding: '14px' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                                            <span style={{ fontSize: '11px', fontWeight: 800, color: '#166534', textTransform: 'uppercase' }}>Online Orders</span>
                                            <span>🚚</span>
                                        </div>
                                        <div style={{ fontSize: '16px', fontWeight: 900, color: '#0f172a' }}>
                                            {channelBreakdown.HOME_DELIVERY?.count || 0} Orders
                                        </div>
                                        <span style={{ fontSize: '12px', color: '#64748b' }}>{inrFormatter.format(channelBreakdown.HOME_DELIVERY?.revenue || 0)}</span>
                                    </div>

                                    <div style={{ background: '#f8fafc', border: '1px solid #edf0f4', borderRadius: '10px', padding: '14px' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                                            <span style={{ fontSize: '11px', fontWeight: 800, color: '#1e40af', textTransform: 'uppercase' }}>Counter Pickups</span>
                                            <span>🏪</span>
                                        </div>
                                        <div style={{ fontSize: '16px', fontWeight: 900, color: '#0f172a' }}>
                                            {channelBreakdown.STORE_PICKUP?.count || 0} Pickups
                                        </div>
                                        <span style={{ fontSize: '12px', color: '#64748b' }}>{inrFormatter.format(channelBreakdown.STORE_PICKUP?.revenue || 0)}</span>
                                    </div>

                                    <div style={{ background: '#f8fafc', border: '1px solid #edf0f4', borderRadius: '10px', padding: '14px' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                                            <span style={{ fontSize: '11px', fontWeight: 800, color: '#92400e', textTransform: 'uppercase' }}>Advance Reserves</span>
                                            <span>🔖</span>
                                        </div>
                                        <div style={{ fontSize: '16px', fontWeight: 900, color: '#0f172a' }}>
                                            {channelBreakdown.RESERVE_ADVANCE?.count || 0} Reserves
                                        </div>
                                        <span style={{ fontSize: '12px', color: '#64748b' }}>{inrFormatter.format(channelBreakdown.RESERVE_ADVANCE?.revenue || 0)}</span>
                                    </div>

                                    <div style={{ background: '#f8fafc', border: '1px solid #edf0f4', borderRadius: '10px', padding: '14px' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                                            <span style={{ fontSize: '11px', fontWeight: 800, color: '#7e22ce', textTransform: 'uppercase' }}>24h Offline Holds</span>
                                            <span>📌</span>
                                        </div>
                                        <div style={{ fontSize: '16px', fontWeight: 900, color: '#0f172a' }}>
                                            {channelBreakdown.BOOKING?.count || 0} Holds
                                        </div>
                                        <span style={{ fontSize: '12px', color: '#64748b' }}>Pay at counter</span>
                                    </div>

                                    <div style={{ background: '#f8fafc', border: '1px solid #edf0f4', borderRadius: '10px', padding: '14px' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                                            <span style={{ fontSize: '11px', fontWeight: 800, color: '#c2410c', textTransform: 'uppercase' }}>Customer Calls</span>
                                            <span>📞</span>
                                        </div>
                                        <div style={{ fontSize: '16px', fontWeight: 900, color: '#0f172a' }}>
                                            {channelBreakdown.CALL_INQUIRY?.count || 0} Leads
                                        </div>
                                        <span style={{ fontSize: '12px', color: '#64748b' }}>Store Visits: {channelBreakdown.STORE_VISIT?.count || 0}</span>
                                    </div>
                                </div>

                                {/* Geographic Inflow Overview */}
                                <div style={{ borderTop: '1px solid #edf0f4', paddingTop: '16px' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap', gap: '8px' }}>
                                        <h4 style={{ fontSize: '14px', fontWeight: 800, color: '#1e293b', margin: 0 }}>
                                            Where this Seller&apos;s Customers Come From (Geographic Inflow)
                                        </h4>
                                        <span style={{ fontSize: '12px', color: '#64748b', fontWeight: 600 }}>
                                            Local Hub: <strong>{customerGeographicInflow.vendorCity || store?.address?.city || 'Local Storefront'}</strong>
                                        </span>
                                    </div>

                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '12.5px', fontWeight: 700, marginBottom: '6px' }}>
                                        <span style={{ color: '#16a34a' }}>🏠 Local Neighborhood: {customerGeographicInflow.localPercentage ?? 100}% ({customerGeographicInflow.localCount || 0})</span>
                                        <span style={{ color: '#2563eb' }}>✈️ Outstation Delivery: {customerGeographicInflow.outstationPercentage ?? 0}% ({customerGeographicInflow.outstationCount || 0})</span>
                                    </div>
                                    <div style={{ height: '8px', width: '100%', background: '#e2e8f0', borderRadius: '99px', overflow: 'hidden', display: 'flex', marginBottom: '16px' }}>
                                        <div style={{ width: `${customerGeographicInflow.localPercentage ?? 100}%`, background: '#16a34a' }} />
                                        <div style={{ width: `${customerGeographicInflow.outstationPercentage ?? 0}%`, background: '#2563eb' }} />
                                    </div>

                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '10px' }}>
                                        {customerGeographicInflow.topCities && customerGeographicInflow.topCities.length > 0 ? (
                                            customerGeographicInflow.topCities.slice(0, 4).map((c, i) => (
                                                <div key={i} style={{ padding: '8px 12px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #edf0f4', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '12.5px' }}>
                                                    <span style={{ fontWeight: 700, color: '#1e293b' }}>📍 {c.city}</span>
                                                    <span style={{ color: '#64748b', fontWeight: 600 }}>{c.count} orders ({inrFormatter.format(c.revenue)})</span>
                                                </div>
                                            ))
                                        ) : (
                                            <div style={{ gridColumn: '1 / -1', padding: '10px', textAlign: 'center', color: '#94a3b8', fontSize: '12px' }}>
                                                Location demand data will appear as orders are fulfilled.
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Row 1: Sales Performance & Product Capacity */}
                            <div className="v360-two-col">
                                {/* Sales Performance Breakdown */}
                                <div className="v360-panel">
                                    <div className="v360-panel-header">
                                        <div>
                                            <h3 className="v360-panel-title">Sales &amp; Earnings Formula</h3>
                                            <span className="v360-panel-sub">Gross Sales − Marketplace Commission = Seller Net Earnings</span>
                                        </div>
                                    </div>
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', margin: '14px 0' }}>
                                        <div style={{ background: '#fbfaf8', padding: '14px', borderRadius: '10px', border: '1px solid #edf0f4' }}>
                                            <div style={{ fontSize: '11px', color: '#737a84', fontWeight: 800, textTransform: 'uppercase' }}>Gross Sales</div>
                                            <div style={{ fontSize: '18px', fontWeight: 900, color: '#111315', marginTop: '4px' }}>
                                                {inrFormatter.format(kpis.grossSales)}
                                            </div>
                                            <span style={{ fontSize: '11.5px', color: '#5a5854' }}>{kpis.totalUnitsSold} units sold</span>
                                        </div>
                                        <div style={{ background: '#fbfaf8', padding: '14px', borderRadius: '10px', border: '1px solid #edf0f4' }}>
                                            <div style={{ fontSize: '11px', color: '#737a84', fontWeight: 800, textTransform: 'uppercase' }}>Commission ({plan.commissionRate}%)</div>
                                            <div style={{ fontSize: '18px', fontWeight: 900, color: '#a85050', marginTop: '4px' }}>
                                                {inrFormatter.format(kpis.totalCommission)}
                                            </div>
                                            <span style={{ fontSize: '11.5px', color: '#a85050', fontWeight: 700 }}>IM Online revenue</span>
                                        </div>
                                        <div style={{ background: '#fbfaf8', padding: '14px', borderRadius: '10px', border: '1px solid #edf0f4' }}>
                                            <div style={{ fontSize: '11px', color: '#737a84', fontWeight: 800, textTransform: 'uppercase' }}>Seller Earnings</div>
                                            <div style={{ fontSize: '18px', fontWeight: 900, color: '#16a34a', marginTop: '4px' }}>
                                                {inrFormatter.format(kpis.sellerEarnings)}
                                            </div>
                                            <span style={{ fontSize: '11.5px', color: '#16a34a', fontWeight: 700 }}>Net seller payout</span>
                                        </div>
                                    </div>
                                    <div style={{ fontSize: '12.5px', color: '#737a84', borderTop: '1px solid #edf0f4', paddingTop: '12px', display: 'flex', justifyContent: 'space-between' }}>
                                        <span>Average Order Value: <strong>{inrFormatter.format(kpis.averageOrderValue)}</strong></span>
                                        <span>Plan: <strong>{plan.name} (₹{plan.price}/mo)</strong></span>
                                    </div>
                                </div>

                                {/* Product Capacity & Limits */}
                                <div className="v360-panel">
                                    <div className="v360-panel-header">
                                        <div>
                                            <h3 className="v360-panel-title">Product Capacity</h3>
                                            <span className="v360-panel-sub">Usage against {plan.name} Tier limit</span>
                                        </div>
                                        <span className={`v360-status-badge ${capacity.percentage >= 90 ? 'rejected' : capacity.percentage >= 75 ? 'pending' : 'approved'}`}>
                                            {capacity.state}
                                        </span>
                                    </div>

                                    <div className="v360-capacity-box">
                                        <div className="v360-capacity-header">
                                            <strong style={{ fontSize: '15px', color: '#111315' }}>
                                                {capacity.productsUsed} / {capacity.productLimit} Products Listed
                                            </strong>
                                            <span style={{ fontSize: '14px', fontWeight: 900, color: capacity.percentage >= 90 ? '#dc2626' : '#111315' }}>
                                                {capacity.percentage}% Used
                                            </span>
                                        </div>

                                        <div className="v360-capacity-bar-track">
                                            <div
                                                className={`v360-capacity-bar-fill ${capacity.percentage >= 100 ? 'danger' : capacity.percentage >= 80 ? 'warning' : ''}`}
                                                style={{ width: `${Math.min(100, capacity.percentage)}%` }}
                                            />
                                        </div>

                                        <div className="v360-capacity-meta-row">
                                            <span>Remaining Capacity: <strong>{capacity.productsRemaining} items</strong></span>
                                            <span>Approved Live: <strong>{kpis.approvedProducts} items</strong></span>
                                        </div>
                                    </div>

                                    {capacity.percentage >= 80 && (
                                        <div style={{ marginTop: '12px', fontSize: '12.5px', color: '#92400e', background: '#fffbeb', padding: '10px', borderRadius: '6px', border: '1px solid #fde68a' }}>
                                            ⚠️ Seller is near listing limit. Recommend upgrading tier to list additional products.
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Row 2: Top Products & Best Seller Highlight */}
                            <div className="v360-two-col">
                                <div className="v360-panel">
                                    <div className="v360-panel-header">
                                        <div>
                                            <h3 className="v360-panel-title">Top Performing Products</h3>
                                            <span className="v360-panel-sub">Ranked by revenue generated</span>
                                        </div>
                                        <button type="button" onClick={() => setActiveTab('products')} className="v360-btn-secondary" style={{ padding: '4px 10px', fontSize: '12px' }}>
                                            View All ({allProducts.length}) &rarr;
                                        </button>
                                    </div>

                                    {/* Best Seller Highlight Card */}
                                    {bestSeller && (
                                        <div className="v360-best-seller-highlight">
                                            {bestSeller.mainImage ? (
                                                <img src={bestSeller.mainImage} alt={bestSeller.name} className="v360-best-seller-img" />
                                            ) : (
                                                <div className="v360-best-seller-img" style={{ background: '#f0f2f5', display: 'grid', placeItems: 'center' }}>
                                                    <Inventory2OutlinedIcon style={{ color: '#8c939f' }} />
                                                </div>
                                            )}
                                            <div className="v360-best-seller-info">
                                                <span className="v360-best-seller-tag">★ BEST SELLER</span>
                                                <h4 className="v360-best-seller-title">{bestSeller.name}</h4>
                                                <div className="v360-best-seller-metrics">
                                                    <span>Orders: <strong>{bestSeller.ordersCount}</strong></span>
                                                    <span>Units: <strong>{bestSeller.unitsSold}</strong></span>
                                                    <span>Revenue: <strong>{inrFormatter.format(bestSeller.revenue)}</strong></span>
                                                    <span>Stock: <strong style={{ color: bestSeller.productStock <= 5 ? '#dc2626' : '#111315' }}>{bestSeller.productStock}</strong></span>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {topProducts.length > 0 ? (
                                        <div className="v360-table-wrap">
                                            <table className="v360-table">
                                                <thead>
                                                    <tr>
                                                        <th>Product</th>
                                                        <th>Price</th>
                                                        <th>Stock</th>
                                                        <th>Orders</th>
                                                        <th>Revenue</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {topProducts.slice(0, 4).map((p) => (
                                                        <tr key={p._id}>
                                                            <td>
                                                                <div className="v360-prod-cell">
                                                                    {p.mainImage ? (
                                                                        <img src={p.mainImage} alt={p.name} className="v360-prod-thumb" />
                                                                    ) : (
                                                                        <div className="v360-prod-thumb" style={{ background: '#f0f2f5', display: 'grid', placeItems: 'center' }}>
                                                                            <Inventory2OutlinedIcon style={{ fontSize: '18px', color: '#8c939f' }} />
                                                                        </div>
                                                                    )}
                                                                    <div>
                                                                        <strong style={{ display: 'block', color: '#111315' }}>{p.name}</strong>
                                                                        <span style={{ fontSize: '11.5px', color: '#737a84' }}>{p.category}</span>
                                                                    </div>
                                                                </div>
                                                            </td>
                                                            <td>{inrFormatter.format(p.price)}</td>
                                                            <td>
                                                                <span style={{ color: p.productStock <= 5 ? '#dc2626' : '#111315', fontWeight: 800 }}>
                                                                    {p.productStock}
                                                                </span>
                                                            </td>
                                                            <td>{p.ordersCount}</td>
                                                            <td><strong>{inrFormatter.format(p.revenue)}</strong></td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    ) : (
                                        <div style={{ textAlign: 'center', padding: '30px', color: '#737a84' }}>
                                            No products added yet.
                                        </div>
                                    )}
                                </div>

                                {/* Products Awaiting Approval Queue */}
                                <div className="v360-panel">
                                    <div className="v360-panel-header">
                                        <div>
                                            <h3 className="v360-panel-title">Products Awaiting Review</h3>
                                            <span className="v360-panel-sub">{pendingApprovalQueue.length} items in approval queue</span>
                                        </div>
                                        <button type="button" onClick={() => { setActiveTab('products'); setProductFilter('pending'); }} className="v360-btn-secondary" style={{ padding: '4px 10px', fontSize: '12px' }}>
                                            Review All &rarr;
                                        </button>
                                    </div>

                                    {pendingApprovalQueue.length > 0 ? (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                            {pendingApprovalQueue.slice(0, 3).map((item) => (
                                                <div key={item._id} style={{ background: '#fbfaf8', border: '1px solid #edf0f4', borderRadius: '8px', padding: '12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px' }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                        {item.mainImage ? (
                                                            <img src={item.mainImage} alt={item.name} className="v360-prod-thumb" />
                                                        ) : (
                                                            <div className="v360-prod-thumb" style={{ background: '#f0f2f5', display: 'grid', placeItems: 'center' }}>
                                                                <Inventory2OutlinedIcon style={{ fontSize: '18px', color: '#8c939f' }} />
                                                            </div>
                                                        )}
                                                        <div>
                                                            <strong style={{ fontSize: '13.5px', color: '#111315', display: 'block' }}>{item.name}</strong>
                                                            <span style={{ fontSize: '12px', color: '#737a84' }}>{inrFormatter.format(item.price)} &middot; {item.category}</span>
                                                        </div>
                                                    </div>
                                                    <div style={{ display: 'flex', gap: '6px' }}>
                                                        <button
                                                            type="button"
                                                            className="v360-btn-success"
                                                            style={{ padding: '4px 8px', fontSize: '11.5px' }}
                                                            onClick={() => handleProductApproval(item._id, 'approved')}
                                                            disabled={actionLoading}
                                                        >
                                                            <CheckIcon style={{ fontSize: '14px' }} /> Approve
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="v360-btn-danger"
                                                            style={{ padding: '4px 8px', fontSize: '11.5px' }}
                                                            onClick={() => handleProductApproval(item._id, 'rejected')}
                                                            disabled={actionLoading}
                                                        >
                                                            <CloseIcon style={{ fontSize: '14px' }} /> Reject
                                                        </button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div style={{ textAlign: 'center', padding: '36px 12px', color: '#737a84' }}>
                                            <CheckCircleOutlineIcon style={{ fontSize: '32px', color: '#16a34a', marginBottom: '6px' }} />
                                            <div style={{ fontSize: '14px', fontWeight: 800, color: '#111315' }}>Queue Is Clear</div>
                                            <p style={{ fontSize: '12.5px', margin: '4px 0 0' }}>All product submissions from this seller are reviewed.</p>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Row 3: Decision Support & Growth Opportunities */}
                            <div className="v360-col-equal">
                                {/* Seller Health & Decision Support */}
                                <div className="v360-panel">
                                    <div className="v360-panel-header">
                                        <div>
                                            <h3 className="v360-panel-title">Seller Health &amp; Decision Support</h3>
                                            <span className="v360-panel-sub">Measurable signals to guide Admin actions</span>
                                        </div>
                                    </div>

                                    <div className="v360-health-list">
                                        {healthCheckpoints.map((chk, idx) => (
                                            <div key={idx} className={`v360-health-item ${chk.status}`}>
                                                {chk.status === 'good' && <CheckCircleOutlineIcon style={{ fontSize: '16px' }} />}
                                                {chk.status === 'attention' && <WarningAmberOutlinedIcon style={{ fontSize: '16px' }} />}
                                                {chk.status === 'risk' && <ErrorOutlineIcon style={{ fontSize: '16px' }} />}
                                                <span>{chk.text}</span>
                                            </div>
                                        ))}
                                        {healthCheckpoints.length === 0 && (
                                            <div style={{ color: '#737a84', fontSize: '13px', padding: '10px' }}>
                                                Awaiting additional seller transactions to evaluate health metrics.
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Growth Opportunities */}
                                <div className="v360-panel">
                                    <div className="v360-panel-header">
                                        <div>
                                            <h3 className="v360-panel-title">Growth Opportunities</h3>
                                            <span className="v360-panel-sub">Data-driven business recommendations</span>
                                        </div>
                                        <LightbulbOutlinedIcon style={{ color: '#a85050' }} />
                                    </div>

                                    <div className="v360-opps-list">
                                        {growthOpportunities.map((opp, idx) => (
                                            <div key={idx} className="v360-opp-card">
                                                <div className="v360-opp-title">{opp.title}</div>
                                                <p className="v360-opp-desc">{opp.desc}</p>
                                            </div>
                                        ))}
                                        {growthOpportunities.length === 0 && (
                                            <div style={{ color: '#737a84', fontSize: '13px', padding: '10px' }}>
                                                Seller operations running smoothly without flagged bottlenecks.
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Row 4: Recent Orders & Recent Activity */}
                            <div className="v360-two-col">
                                <div className="v360-panel">
                                    <div className="v360-panel-header">
                                        <div>
                                            <h3 className="v360-panel-title">Recent Orders</h3>
                                            <span className="v360-panel-sub">Multi-vendor item-level checkouts</span>
                                        </div>
                                        <button type="button" onClick={() => setActiveTab('orders')} className="v360-btn-secondary" style={{ padding: '4px 10px', fontSize: '12px' }}>
                                            View All Orders &rarr;
                                        </button>
                                    </div>

                                    {orders.length > 0 ? (
                                        <div className="v360-table-wrap">
                                            <table className="v360-table">
                                                <thead>
                                                    <tr>
                                                        <th>Order ID</th>
                                                        <th>Customer</th>
                                                        <th>Value</th>
                                                        <th>Commission</th>
                                                        <th>Earnings</th>
                                                        <th>Status</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {orders.slice(0, 5).map((o) => (
                                                        <tr key={o._id}>
                                                            <td style={{ fontFamily: 'monospace', fontWeight: 800 }}>
                                                                #{o._id.slice(-6).toUpperCase()}
                                                            </td>
                                                            <td>{o.customer}</td>
                                                            <td><strong>{inrFormatter.format(o.vendorOrderValue)}</strong></td>
                                                            <td style={{ color: '#a85050', fontWeight: 800 }}>{inrFormatter.format(o.commission)}</td>
                                                            <td style={{ color: '#16a34a', fontWeight: 800 }}>{inrFormatter.format(o.sellerEarnings)}</td>
                                                            <td>
                                                                <span className={`v360-status-badge ${o.orderStatus === 'Delivered' ? 'approved' : 'pending'}`}>
                                                                    {o.orderStatus}
                                                                </span>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    ) : (
                                        <div style={{ textAlign: 'center', padding: '30px', color: '#737a84' }}>
                                            No orders received yet.
                                        </div>
                                    )}
                                </div>

                                <div className="v360-panel">
                                    <div className="v360-panel-header">
                                        <div>
                                            <h3 className="v360-panel-title">Recent Activity</h3>
                                            <span className="v360-panel-sub">Audit trail of merchant events</span>
                                        </div>
                                        <HistoryIcon style={{ color: '#737a84' }} />
                                    </div>

                                    {activityTimeline.length > 0 ? (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                            {activityTimeline.map((item, idx) => (
                                                <div key={idx} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', fontSize: '13px' }}>
                                                    <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#a85050', marginTop: '6px', flexShrink: 0 }} />
                                                    <div style={{ flex: 1 }}>
                                                        <div style={{ color: '#111315', fontWeight: 700 }}>{item.event}</div>
                                                        <div style={{ fontSize: '11.5px', color: '#8c939f' }}>
                                                            {new Date(item.date).toLocaleString('en-IN', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div style={{ textAlign: 'center', padding: '30px', color: '#737a84' }}>
                                            No recent activity logs.
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* ═══════════════════════════════════════════════════════════════════ */}
                    {/* TAB 2: PRODUCTS (INVESTIGATION)                                    */}
                    {/* ═══════════════════════════════════════════════════════════════════ */}
                    {activeTab === 'products' && (
                        <div className="v360-panel">
                            <div className="v360-panel-header" style={{ flexWrap: 'wrap', gap: '14px' }}>
                                <div>
                                    <h3 className="v360-panel-title">All Vendor Products ({filteredProducts.length})</h3>
                                    <span className="v360-panel-sub">Complete catalogue listed by this seller</span>
                                </div>

                                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                                    <select
                                        value={productFilter}
                                        onChange={(e) => setProductFilter(e.target.value)}
                                        style={{ padding: '6px 10px', borderRadius: '6px', border: '1px solid #e0e4ea', fontSize: '12.5px', fontWeight: 700 }}
                                    >
                                        <option value="all">All Products</option>
                                        <option value="pending">Pending Approval</option>
                                        <option value="approved">Approved Live</option>
                                        <option value="rejected">Rejected</option>
                                        <option value="low_stock">Low Stock (≤5)</option>
                                        <option value="out_of_stock">Out of Stock (0)</option>
                                    </select>

                                    <select
                                        value={productSort}
                                        onChange={(e) => setProductSort(e.target.value)}
                                        style={{ padding: '6px 10px', borderRadius: '6px', border: '1px solid #e0e4ea', fontSize: '12.5px', fontWeight: 700 }}
                                    >
                                        <option value="revenue">Sort by Revenue</option>
                                        <option value="orders">Sort by Orders</option>
                                        <option value="units">Sort by Units Sold</option>
                                        <option value="rating">Sort by Rating</option>
                                    </select>
                                </div>
                            </div>

                            {filteredProducts.length > 0 ? (
                                <div className="v360-table-wrap">
                                    <table className="v360-table">
                                        <thead>
                                            <tr>
                                                <th>Product</th>
                                                <th>Category</th>
                                                <th>Price</th>
                                                <th>Stock</th>
                                                <th>Status</th>
                                                <th>Orders</th>
                                                <th>Revenue</th>
                                                <th>Rating</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredProducts.map((p) => (
                                                <tr key={p._id}>
                                                    <td>
                                                        <div className="v360-prod-cell">
                                                            {p.mainImage ? (
                                                                <img src={p.mainImage} alt={p.name} className="v360-prod-thumb" />
                                                            ) : (
                                                                <div className="v360-prod-thumb" style={{ background: '#f0f2f5', display: 'grid', placeItems: 'center' }}>
                                                                    <Inventory2OutlinedIcon style={{ fontSize: '18px', color: '#8c939f' }} />
                                                                </div>
                                                            )}
                                                            <div>
                                                                <strong style={{ color: '#111315', display: 'block' }}>{p.name}</strong>
                                                                {p.rejectionReason && (
                                                                    <span style={{ fontSize: '11px', color: '#dc2626' }}>Reason: {p.rejectionReason}</span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td>{p.category}</td>
                                                    <td>
                                                        <strong>{inrFormatter.format(p.price)}</strong>
                                                        {p.discountPrice > 0 && <small style={{ display: 'block', color: '#737a84', textDecoration: 'line-through' }}>{inrFormatter.format(p.discountPrice)}</small>}
                                                    </td>
                                                    <td>
                                                        <span style={{ color: p.productStock <= 5 ? '#dc2626' : '#111315', fontWeight: 800 }}>
                                                            {p.productStock}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <span className={`v360-status-badge ${p.approvalStatus}`}>
                                                            {p.approvalStatus}
                                                        </span>
                                                    </td>
                                                    <td>{p.ordersCount}</td>
                                                    <td><strong>{inrFormatter.format(p.revenue)}</strong></td>
                                                    <td>{p.ratings > 0 ? `${p.ratings} ★` : '—'}</td>
                                                    <td>
                                                        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                                                            {p.approvalStatus === 'blocked' ? (
                                                                <button
                                                                    type="button"
                                                                    className="v360-btn-success"
                                                                    style={{ padding: '4px 8px', fontSize: '11px' }}
                                                                    onClick={() => handleUnblockProduct(p._id)}
                                                                    disabled={actionLoading}
                                                                >
                                                                    Unblock
                                                                </button>
                                                            ) : (
                                                                <>
                                                                    {p.approvalStatus !== 'approved' && (
                                                                        <button
                                                                            type="button"
                                                                            className="v360-btn-success"
                                                                            style={{ padding: '4px 8px', fontSize: '11px' }}
                                                                            onClick={() => handleProductApproval(p._id, 'approved')}
                                                                            disabled={actionLoading}
                                                                        >
                                                                            Approve
                                                                        </button>
                                                                    )}
                                                                    {p.approvalStatus !== 'rejected' && (
                                                                        <button
                                                                            type="button"
                                                                            className="v360-btn-danger"
                                                                            style={{ padding: '4px 8px', fontSize: '11px' }}
                                                                            onClick={() => handleProductApproval(p._id, 'rejected')}
                                                                            disabled={actionLoading}
                                                                        >
                                                                            Reject
                                                                        </button>
                                                                    )}
                                                                    <button
                                                                        type="button"
                                                                        className="v360-btn-danger"
                                                                        style={{ padding: '4px 8px', fontSize: '11px', background: '#dc2626', borderColor: '#dc2626', color: '#fff' }}
                                                                        onClick={() => {
                                                                            setProductBlockTarget(p);
                                                                            setProductBlockOpen(true);
                                                                        }}
                                                                        disabled={actionLoading}
                                                                        title="Block product from marketplace"
                                                                    >
                                                                        Block Listing
                                                                    </button>
                                                                </>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <div style={{ textAlign: 'center', padding: '40px', color: '#737a84' }}>
                                    No products matching this filter.
                                </div>
                            )}
                        </div>
                    )}

                    {/* ═══════════════════════════════════════════════════════════════════ */}
                    {/* TAB 3: ORDERS (INVESTIGATION)                                      */}
                    {/* ═══════════════════════════════════════════════════════════════════ */}
                    {activeTab === 'orders' && (
                        <div className="v360-panel">
                            <div className="v360-panel-header" style={{ flexWrap: 'wrap', gap: '14px' }}>
                                <div>
                                    <h3 className="v360-panel-title">Vendor Orders ({filteredOrders.length})</h3>
                                    <span className="v360-panel-sub">Calculated specifically for this seller's products</span>
                                </div>

                                <select
                                    value={orderStatusFilter}
                                    onChange={(e) => setOrderStatusFilter(e.target.value)}
                                    style={{ padding: '6px 10px', borderRadius: '6px', border: '1px solid #e0e4ea', fontSize: '12.5px', fontWeight: 700 }}
                                >
                                    <option value="all">All Statuses</option>
                                    <option value="Processing">Processing</option>
                                    <option value="Shipped">Shipped</option>
                                    <option value="Delivered">Delivered</option>
                                    <option value="Cancelled">Cancelled</option>
                                </select>
                            </div>

                            {filteredOrders.length > 0 ? (
                                <div className="v360-table-wrap">
                                    <table className="v360-table">
                                        <thead>
                                            <tr>
                                                <th>Order ID</th>
                                                <th>Customer</th>
                                                <th>Seller Items</th>
                                                <th>Order Value</th>
                                                <th>Commission ({plan.commissionRate}%)</th>
                                                <th>Seller Earnings</th>
                                                <th>Status</th>
                                                <th>Date</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredOrders.map((o) => (
                                                <tr key={o._id}>
                                                    <td style={{ fontFamily: 'monospace', fontWeight: 800 }}>
                                                        #{o._id.slice(-6).toUpperCase()}
                                                    </td>
                                                    <td>
                                                        <strong>{o.customer}</strong>
                                                        <small style={{ display: 'block', color: '#737a84' }}>{o.customerEmail}</small>
                                                    </td>
                                                    <td>
                                                        {o.items.map((it, i) => (
                                                            <div key={i} style={{ fontSize: '12px' }}>
                                                                {it.quantity}x {it.productName} ({inrFormatter.format(it.price)})
                                                            </div>
                                                        ))}
                                                    </td>
                                                    <td><strong>{inrFormatter.format(o.vendorOrderValue)}</strong></td>
                                                    <td style={{ color: '#a85050', fontWeight: 800 }}>{inrFormatter.format(o.commission)}</td>
                                                    <td style={{ color: '#16a34a', fontWeight: 800 }}>{inrFormatter.format(o.sellerEarnings)}</td>
                                                    <td>
                                                        <span className={`v360-status-badge ${o.orderStatus === 'Delivered' ? 'approved' : 'pending'}`}>
                                                            {o.orderStatus}
                                                        </span>
                                                    </td>
                                                    <td style={{ fontSize: '12px', color: '#737a84' }}>
                                                        {new Date(o.createdAt).toLocaleDateString('en-IN', { month: 'short', day: 'numeric', year: 'numeric' })}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <div style={{ textAlign: 'center', padding: '40px', color: '#737a84' }}>
                                    No orders found for this seller.
                                </div>
                            )}
                        </div>
                    )}

                    {/* ═══════════════════════════════════════════════════════════════════ */}
                    {/* TAB 4: SALES & COMMISSION                                          */}
                    {/* ═══════════════════════════════════════════════════════════════════ */}
                    {activeTab === 'sales' && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                            <div className="v360-panel">
                                <div className="v360-panel-header">
                                    <div>
                                        <h3 className="v360-panel-title">Revenue &amp; Payout Analytics</h3>
                                        <span className="v360-panel-sub">Transparent breakdown of sales, marketplace commission, and net earnings</span>
                                    </div>
                                </div>

                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px', margin: '14px 0' }}>
                                    <div style={{ background: '#fbfaf8', padding: '16px', borderRadius: '10px', border: '1px solid #edf0f4' }}>
                                        <div style={{ fontSize: '11px', color: '#737a84', fontWeight: 800, textTransform: 'uppercase' }}>Gross Sales</div>
                                        <div style={{ fontSize: '24px', fontWeight: 900, color: '#111315', margin: '4px 0' }}>
                                            {inrFormatter.format(kpis.grossSales)}
                                        </div>
                                        <span style={{ fontSize: '12px', color: '#5a5854' }}>Total order item checkout value</span>
                                    </div>

                                    <div style={{ background: '#fbfaf8', padding: '16px', borderRadius: '10px', border: '1px solid #edf0f4' }}>
                                        <div style={{ fontSize: '11px', color: '#737a84', fontWeight: 800, textTransform: 'uppercase' }}>Marketplace Commission</div>
                                        <div style={{ fontSize: '24px', fontWeight: 900, color: '#a85050', margin: '4px 0' }}>
                                            {inrFormatter.format(kpis.totalCommission)}
                                        </div>
                                        <span style={{ fontSize: '12px', color: '#a85050', fontWeight: 700 }}>Retained by IM Online ({plan.commissionRate}%)</span>
                                    </div>

                                    <div style={{ background: '#fbfaf8', padding: '16px', borderRadius: '10px', border: '1px solid #edf0f4' }}>
                                        <div style={{ fontSize: '11px', color: '#737a84', fontWeight: 800, textTransform: 'uppercase' }}>Net Seller Payout</div>
                                        <div style={{ fontSize: '24px', fontWeight: 900, color: '#16a34a', margin: '4px 0' }}>
                                            {inrFormatter.format(kpis.sellerEarnings)}
                                        </div>
                                        <span style={{ fontSize: '12px', color: '#16a34a', fontWeight: 700 }}>Due to seller bank account</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* ═══════════════════════════════════════════════════════════════════ */}
                    {/* TAB 5: REVIEWS & RATINGS                                           */}
                    {/* ═══════════════════════════════════════════════════════════════════ */}
                    {activeTab === 'reviews' && (
                        <div className="v360-panel">
                            <div className="v360-panel-header">
                                <div>
                                    <h3 className="v360-panel-title">Customer Reviews &amp; Feedback</h3>
                                    <span className="v360-panel-sub">Average rating: {kpis.averageRating} ★ across {kpis.totalReviews} reviews</span>
                                </div>
                            </div>

                            {reviews.recentReviews && reviews.recentReviews.length > 0 ? (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                    {reviews.recentReviews.map((rev, idx) => (
                                        <div key={idx} style={{ background: '#fbfaf8', border: '1px solid #edf0f4', borderRadius: '8px', padding: '14px' }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                    <strong>{rev.customerName}</strong>
                                                    <span style={{ color: '#d97706', fontWeight: 800 }}>{'★'.repeat(rev.rating)}</span>
                                                </div>
                                                <span style={{ fontSize: '12px', color: '#8c939f' }}>
                                                    {new Date(rev.createdAt).toLocaleDateString('en-IN', { month: 'short', day: 'numeric', year: 'numeric' })}
                                                </span>
                                            </div>
                                            <div style={{ fontSize: '13px', color: '#5a5854', marginBottom: '4px' }}>"{rev.comment}"</div>
                                            <small style={{ color: '#737a84' }}>Product: {rev.productName}</small>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div style={{ textAlign: 'center', padding: '40px', color: '#737a84' }}>
                                    No customer reviews recorded yet for this seller's products.
                                </div>
                            )}
                        </div>
                    )}

                    {/* ═══════════════════════════════════════════════════════════════════ */}
                    {/* TAB 6: RETURNS & CANCELLATIONS                                     */}
                    {/* ═══════════════════════════════════════════════════════════════════ */}
                    {activeTab === 'returns' && (
                        <div className="v360-panel">
                            <div className="v360-panel-header">
                                <div>
                                    <h3 className="v360-panel-title">Returns &amp; Quality ({returns.returnsCount})</h3>
                                    <span className="v360-panel-sub">Return Rate: {kpis.returnRate}% &middot; Cancellation Rate: {kpis.cancellationRate}%</span>
                                </div>
                            </div>

                            {returns.list && returns.list.length > 0 ? (
                                <div className="v360-table-wrap">
                                    <table className="v360-table">
                                        <thead>
                                            <tr>
                                                <th>Customer</th>
                                                <th>Return Type</th>
                                                <th>Reason</th>
                                                <th>Status</th>
                                                <th>Date</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {returns.list.map((ret) => (
                                                <tr key={ret._id}>
                                                    <td>
                                                        <strong>{ret.userName}</strong>
                                                        <small style={{ display: 'block', color: '#737a84' }}>{ret.userEmail}</small>
                                                    </td>
                                                    <td>{ret.returnType}</td>
                                                    <td>{ret.returnReason || 'Product Return'}</td>
                                                    <td>
                                                        <span className="v360-status-badge pending">
                                                            {ret.returnInformation?.status || 'Processing'}
                                                        </span>
                                                    </td>
                                                    <td style={{ fontSize: '12px', color: '#737a84' }}>
                                                        {new Date(ret.returnInformation?.createdAt || Date.now()).toLocaleDateString('en-IN', { month: 'short', day: 'numeric', year: 'numeric' })}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <div style={{ textAlign: 'center', padding: '40px', color: '#737a84' }}>
                                    <CheckCircleOutlineIcon style={{ fontSize: '32px', color: '#16a34a', marginBottom: '6px' }} />
                                    <div style={{ fontSize: '14px', fontWeight: 800, color: '#111315' }}>Zero Returns Recorded</div>
                                    <p style={{ fontSize: '12.5px', margin: '4px 0 0' }}>This seller has maintaining a flawless return and quality record.</p>
                                </div>
                            )}
                        </div>
                    )}

                    {/* ═══════════════════════════════════════════════════════════════════ */}
                    {/* TAB 7: STORE PROFILE                                               */}
                    {/* ═══════════════════════════════════════════════════════════════════ */}
                    {activeTab === 'store' && (
                        <div className="v360-panel">
                            <div className="v360-panel-header">
                                <div>
                                    <h3 className="v360-panel-title">Store Profile &amp; Branding</h3>
                                    <span className="v360-panel-sub">Public customer-facing storefront details</span>
                                </div>
                            </div>

                            {store ? (
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px', marginTop: '10px' }}>
                                    <div>
                                        <label style={{ fontSize: '11px', fontWeight: 800, color: '#737a84', textTransform: 'uppercase' }}>Store Name</label>
                                        <div style={{ fontSize: '16px', fontWeight: 800, color: '#111315', marginTop: '2px' }}>{store.name}</div>
                                    </div>
                                    <div>
                                        <label style={{ fontSize: '11px', fontWeight: 800, color: '#737a84', textTransform: 'uppercase' }}>Store Slug</label>
                                        <div style={{ fontSize: '14px', fontFamily: 'monospace', color: '#111315', marginTop: '2px' }}>/{store.slug}</div>
                                    </div>
                                    <div>
                                        <label style={{ fontSize: '11px', fontWeight: 800, color: '#737a84', textTransform: 'uppercase' }}>Description</label>
                                        <div style={{ fontSize: '13.5px', color: '#5a5854', marginTop: '2px' }}>{store.description || 'No description provided.'}</div>
                                    </div>
                                    <div>
                                        <label style={{ fontSize: '11px', fontWeight: 800, color: '#737a84', textTransform: 'uppercase' }}>Store Status</label>
                                        <div style={{ marginTop: '2px' }}>
                                            <span className={`v360-status-badge ${store.status === 'active' ? 'approved' : 'pending'}`}>
                                                {store.status}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div style={{ textAlign: 'center', padding: '40px', color: '#737a84' }}>
                                    Store profile has not been initialized yet by the seller.
                                </div>
                            )}
                        </div>
                    )}

                    {/* ═══════════════════════════════════════════════════════════════════ */}
                    {/* TAB 8: PLAN & BILLING                                              */}
                    {/* ═══════════════════════════════════════════════════════════════════ */}
                    {activeTab === 'plan' && (
                        <div className="v360-panel">
                            <div className="v360-panel-header">
                                <div>
                                    <h3 className="v360-panel-title">Seller Plan &amp; Subscription</h3>
                                    <span className="v360-panel-sub">Current tier benefits and listing allowances</span>
                                </div>
                                <Link to="/Admin/dashboard/seller-plans" className="v360-btn-secondary" style={{ fontSize: '12px' }}>
                                    Manage Marketplace Plans &rarr;
                                </Link>
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px', margin: '14px 0' }}>
                                <div style={{ background: '#fbfaf8', padding: '16px', borderRadius: '10px', border: '1px solid #edf0f4' }}>
                                    <div style={{ fontSize: '11px', color: '#737a84', fontWeight: 800, textTransform: 'uppercase' }}>Active Plan</div>
                                    <div style={{ fontSize: '20px', fontWeight: 900, color: '#111315', marginTop: '4px' }}>
                                        {plan.name} Tier
                                    </div>
                                    <span style={{ fontSize: '12px', color: '#16a34a', fontWeight: 700 }}>● {plan.status}</span>
                                </div>

                                <div style={{ background: '#fbfaf8', padding: '16px', borderRadius: '10px', border: '1px solid #edf0f4' }}>
                                    <div style={{ fontSize: '11px', color: '#737a84', fontWeight: 800, textTransform: 'uppercase' }}>Subscription Price</div>
                                    <div style={{ fontSize: '20px', fontWeight: 900, color: '#111315', marginTop: '4px' }}>
                                        {plan.price === 0 ? '₹0 Free' : `₹${plan.price}/mo`}
                                    </div>
                                </div>

                                <div style={{ background: '#fbfaf8', padding: '16px', borderRadius: '10px', border: '1px solid #edf0f4' }}>
                                    <div style={{ fontSize: '11px', color: '#737a84', fontWeight: 800, textTransform: 'uppercase' }}>Product Listing Limit</div>
                                    <div style={{ fontSize: '20px', fontWeight: 900, color: '#111315', marginTop: '4px' }}>
                                        {capacity.productsUsed} / {capacity.productLimit}
                                    </div>
                                </div>

                                <div style={{ background: '#fbfaf8', padding: '16px', borderRadius: '10px', border: '1px solid #edf0f4' }}>
                                    <div style={{ fontSize: '11px', color: '#737a84', fontWeight: 800, textTransform: 'uppercase' }}>Commission Rate</div>
                                    <div style={{ fontSize: '20px', fontWeight: 900, color: '#a85050', marginTop: '4px' }}>
                                        {plan.commissionRate}%
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* ═══════════════════════════════════════════════════════════════════ */}
                    {/* TAB 9: VERIFICATION & CREDENTIALS                                  */}
                    {/* ═══════════════════════════════════════════════════════════════════ */}
                    {activeTab === 'verification' && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                            {/* Listing Gate & Operational Status */}
                            <div className="v360-panel">
                                <div className="v360-panel-header">
                                    <div>
                                        <h3 className="v360-panel-title">Product Listing Gate &amp; Compliance</h3>
                                        <span className="v360-panel-sub">Control whether this vendor is allowed to add &amp; publish products</span>
                                    </div>
                                    <span style={{
                                        background: data?.sellerHealth?.listingEnabled ? '#dcfce7' : '#fee2e2',
                                        color: data?.sellerHealth?.listingEnabled ? '#166534' : '#991b1b',
                                        padding: '4px 12px',
                                        borderRadius: '99px',
                                        fontSize: '12px',
                                        fontWeight: 800
                                    }}>
                                        {data?.sellerHealth?.listingEnabled ? '🟢 Listing Enabled' : '🔒 Listing Locked'}
                                    </span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px', marginTop: '10px' }}>
                                    <p style={{ fontSize: '13px', color: '#5a5854', margin: 0, maxWidth: '600px' }}>
                                        {data?.sellerHealth?.listingEnabled
                                            ? 'Seller has met identity and bank payout requirements. Product creation and submission are active.'
                                            : 'Product listing is locked until Aadhaar, PAN, and Bank details are verified, or overridden by Admin.'}
                                    </p>
                                    <button
                                        type="button"
                                        onClick={() => handleToggleListing(data?.sellerHealth?.listingEnabled)}
                                        className="btn btn-sm"
                                        disabled={actionLoading}
                                        style={{
                                            fontSize: '12.5px',
                                            padding: '6px 16px',
                                            borderRadius: '6px',
                                            fontWeight: 700,
                                            background: data?.sellerHealth?.listingEnabled ? '#fee2e2' : '#16a34a',
                                            color: data?.sellerHealth?.listingEnabled ? '#991b1b' : '#ffffff',
                                            border: 'none',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        {data?.sellerHealth?.listingEnabled ? 'Lock Product Listing' : 'Override: Enable Listing'}
                                    </button>
                                </div>
                            </div>

                            {/* Official Identity Documents Review */}
                            <div className="v360-panel">
                                <div className="v360-panel-header">
                                    <div>
                                        <h3 className="v360-panel-title">Official Identity &amp; Compliance Documents</h3>
                                        <span className="v360-panel-sub">Verify Aadhaar, PAN, and merchant tax certificates</span>
                                    </div>
                                    <SecurityIcon style={{ color: '#16a34a' }} />
                                </div>

                                <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', marginTop: '12px' }}>
                                    {(vendor.documents || []).length > 0 ? (
                                        (vendor.documents || []).map((doc, idx) => (
                                            <div
                                                key={idx}
                                                style={{
                                                    background: '#fbfaf8',
                                                    border: '1px solid #edf0f4',
                                                    borderRadius: '10px',
                                                    padding: '16px',
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    flexWrap: 'wrap',
                                                    gap: '14px'
                                                }}
                                            >
                                                <div>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                        <strong style={{ fontSize: '15px', color: '#111315' }}>{doc.documentName}</strong>
                                                        <span style={{
                                                            background: doc.status === 'verified' ? '#dcfce7' : doc.status === 'rejected' ? '#fee2e2' : '#fef3c7',
                                                            color: doc.status === 'verified' ? '#166534' : doc.status === 'rejected' ? '#991b1b' : '#92400e',
                                                            fontSize: '11px',
                                                            fontWeight: 800,
                                                            padding: '2px 8px',
                                                            borderRadius: '99px',
                                                            textTransform: 'uppercase'
                                                        }}>
                                                            {doc.status}
                                                        </span>
                                                    </div>
                                                    <div style={{ fontSize: '13px', fontFamily: 'monospace', color: '#5a5854', marginTop: '4px' }}>
                                                        Masked ID: {doc.documentNumberMasked || 'Not Submitted'}
                                                    </div>
                                                    {doc.rejectionReason && (
                                                        <div style={{ fontSize: '12px', color: '#dc2626', marginTop: '4px' }}>
                                                            <strong>Rejection Feedback:</strong> {doc.rejectionReason}
                                                        </div>
                                                    )}
                                                </div>

                                                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                                    {doc.status !== 'verified' && (
                                                        <button
                                                            type="button"
                                                            onClick={() => handleVerifyDocument(doc.documentType, 'verified')}
                                                            className="btn btn-sm"
                                                            disabled={actionLoading}
                                                            style={{ fontSize: '12px', padding: '5px 12px', background: '#16a34a', color: '#fff', border: 'none', borderRadius: '6px', fontWeight: 700, cursor: 'pointer' }}
                                                        >
                                                            ✓ Approve
                                                        </button>
                                                    )}
                                                    {doc.status !== 'requires_reupload' && (
                                                        <button
                                                            type="button"
                                                            onClick={() => handleVerifyDocument(doc.documentType, 'requires_reupload')}
                                                            className="btn btn-sm"
                                                            disabled={actionLoading}
                                                            style={{ fontSize: '12px', padding: '5px 12px', background: '#fef3c7', color: '#92400e', border: '1px solid #fde68a', borderRadius: '6px', fontWeight: 700, cursor: 'pointer' }}
                                                        >
                                                            ⟳ Request Re-upload
                                                        </button>
                                                    )}
                                                    {doc.status !== 'rejected' && (
                                                        <button
                                                            type="button"
                                                            onClick={() => handleVerifyDocument(doc.documentType, 'rejected')}
                                                            className="btn btn-sm"
                                                            disabled={actionLoading}
                                                            style={{ fontSize: '12px', padding: '5px 12px', background: '#fee2e2', color: '#991b1b', border: '1px solid #fecaca', borderRadius: '6px', fontWeight: 700, cursor: 'pointer' }}
                                                        >
                                                            ✕ Reject
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        ))
                                    ) : (
                                        <div style={{ textAlign: 'center', padding: '20px', color: '#737a84' }}>
                                            No compliance documents submitted yet.
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Bank Payout Account Verification */}
                            <div className="v360-panel">
                                <div className="v360-panel-header">
                                    <div>
                                        <h3 className="v360-panel-title">Bank &amp; Settlement Payout Details</h3>
                                        <span className="v360-panel-sub">Verify bank credentials for vendor order earnings</span>
                                    </div>
                                    <span style={{
                                        background: vendor.bankDetails?.verificationStatus === 'verified' ? '#dcfce7' : vendor.bankDetails?.verificationStatus === 'rejected' ? '#fee2e2' : '#fef3c7',
                                        color: vendor.bankDetails?.verificationStatus === 'verified' ? '#166534' : vendor.bankDetails?.verificationStatus === 'rejected' ? '#991b1b' : '#92400e',
                                        fontSize: '11px',
                                        fontWeight: 800,
                                        padding: '4px 12px',
                                        borderRadius: '99px',
                                        textTransform: 'uppercase'
                                    }}>
                                        {vendor.bankDetails?.verificationStatus === 'verified' ? '✓ Verified' : vendor.bankDetails?.verificationStatus === 'under_review' ? '● Under Review' : vendor.bankDetails?.verificationStatus === 'rejected' ? '✕ Rejected' : (vendor.bankDetails?.verificationStatus || 'Not Submitted')}
                                    </span>
                                </div>

                                {vendor.bankDetails && (vendor.bankDetails.accountNumberMasked || vendor.bankDetails.accountHolderName || vendor.bankDetails.bankName || vendor.bankDetails.accountNumber) ? (
                                    <div style={{ marginTop: '14px' }}>
                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', background: '#fbfaf8', border: '1px solid #edf0f4', borderRadius: '10px', padding: '16px', marginBottom: '14px' }}>
                                            <div>
                                                <label style={{ fontSize: '11px', fontWeight: 800, color: '#737a84', textTransform: 'uppercase' }}>Account Holder</label>
                                                <div style={{ fontSize: '14px', fontWeight: 800, color: '#111315', marginTop: '2px' }}>{vendor.bankDetails.accountHolderName}</div>
                                            </div>
                                            <div>
                                                <label style={{ fontSize: '11px', fontWeight: 800, color: '#737a84', textTransform: 'uppercase' }}>Bank Name</label>
                                                <div style={{ fontSize: '14px', color: '#111315', marginTop: '2px' }}>{vendor.bankDetails.bankName}</div>
                                            </div>
                                            <div>
                                                <label style={{ fontSize: '11px', fontWeight: 800, color: '#737a84', textTransform: 'uppercase' }}>Account Number</label>
                                                <div style={{ fontSize: '14px', fontFamily: 'monospace', color: '#111315', marginTop: '2px' }}>{vendor.bankDetails.accountNumberMasked}</div>
                                            </div>
                                            <div>
                                                <label style={{ fontSize: '11px', fontWeight: 800, color: '#737a84', textTransform: 'uppercase' }}>IFSC Code</label>
                                                <div style={{ fontSize: '14px', fontFamily: 'monospace', color: '#111315', marginTop: '2px' }}>{vendor.bankDetails.ifsc}</div>
                                            </div>
                                            <div>
                                                <label style={{ fontSize: '11px', fontWeight: 800, color: '#737a84', textTransform: 'uppercase' }}>Account Type</label>
                                                <div style={{ fontSize: '14px', color: '#111315', marginTop: '2px', textTransform: 'capitalize' }}>{vendor.bankDetails.accountType || 'Current'}</div>
                                            </div>
                                            {vendor.bankDetails.upiId && (
                                                <div>
                                                    <label style={{ fontSize: '11px', fontWeight: 800, color: '#737a84', textTransform: 'uppercase' }}>UPI ID</label>
                                                    <div style={{ fontSize: '14px', color: '#111315', marginTop: '2px' }}>{vendor.bankDetails.upiId}</div>
                                                </div>
                                            )}
                                        </div>

                                        {/* Gateway Verification Signals */}
                                        <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '14px 16px', marginBottom: '16px' }}>
                                            <div style={{ fontSize: '12px', fontWeight: 800, color: '#475569', textTransform: 'uppercase', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                <span>⚡ Automated Gateway Verification Signals</span>
                                            </div>
                                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px', fontSize: '13px' }}>
                                                <div>
                                                    <span style={{ color: '#64748b', fontSize: '11px', display: 'block' }}>Provider</span>
                                                    <strong>{vendor.bankDetails.verificationProvider ? vendor.bankDetails.verificationProvider.toUpperCase() : 'CASHFREE'}</strong>
                                                </div>
                                                <div>
                                                    <span style={{ color: '#64748b', fontSize: '11px', display: 'block' }}>Registered Name at Bank</span>
                                                    <strong>{vendor.bankDetails.nameAtBank || vendor.bankDetails.accountHolderName}</strong>
                                                </div>
                                                <div>
                                                    <span style={{ color: '#64748b', fontSize: '11px', display: 'block' }}>Name Match Score</span>
                                                    <span style={{
                                                        display: 'inline-block',
                                                        padding: '2px 8px',
                                                        borderRadius: '4px',
                                                        fontSize: '11.5px',
                                                        fontWeight: 700,
                                                        background: vendor.bankDetails.nameMatchStatus === 'MISMATCH' ? '#fee2e2' : '#dcfce7',
                                                        color: vendor.bankDetails.nameMatchStatus === 'MISMATCH' ? '#991b1b' : '#166534'
                                                    }}>
                                                        {vendor.bankDetails.nameMatchStatus || 'MATCH'} ({vendor.bankDetails.nameMatchScore || 100}%)
                                                    </span>
                                                </div>
                                                <div>
                                                    <span style={{ color: '#64748b', fontSize: '11px', display: 'block' }}>Reference / UTR</span>
                                                    <span style={{ fontFamily: 'monospace', fontSize: '12px', color: '#334155' }}>
                                                        {vendor.bankDetails.verificationReferenceId || vendor.bankDetails.utr || 'VERIFIED_API'}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>

                                        {vendor.bankDetails.rejectionReason && (
                                            <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', padding: '12px 14px', fontSize: '13px', color: '#991b1b', marginBottom: '14px' }}>
                                                <strong>✕ Rejection Reason:</strong> {vendor.bankDetails.rejectionReason}
                                            </div>
                                        )}

                                        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                                            <button
                                                type="button"
                                                onClick={() => setBankModalOpen(true)}
                                                className="btn btn-sm"
                                                style={{ fontSize: '12.5px', padding: '7px 18px', background: '#1e40af', color: '#fff', border: 'none', borderRadius: '6px', fontWeight: 700, cursor: 'pointer' }}
                                            >
                                                🔍 Review Bank Account Details
                                            </button>

                                            {vendor.bankDetails.verificationStatus !== 'verified' && (
                                                <button
                                                    type="button"
                                                    onClick={() => setApproveConfirmOpen(true)}
                                                    className="btn btn-sm"
                                                    disabled={bankActionLoading}
                                                    style={{ fontSize: '12.5px', padding: '7px 18px', background: '#16a34a', color: '#fff', border: 'none', borderRadius: '6px', fontWeight: 700, cursor: 'pointer' }}
                                                >
                                                    ✓ Approve Bank Account
                                                </button>
                                            )}

                                            {vendor.bankDetails.verificationStatus !== 'rejected' && (
                                                <button
                                                    type="button"
                                                    onClick={() => setRejectModalOpen(true)}
                                                    className="btn btn-sm"
                                                    disabled={bankActionLoading}
                                                    style={{ fontSize: '12.5px', padding: '7px 18px', background: '#fee2e2', color: '#991b1b', border: '1px solid #fecaca', borderRadius: '6px', fontWeight: 700, cursor: 'pointer' }}
                                                >
                                                    ✕ Reject Bank Details
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                ) : (
                                    <div style={{ textAlign: 'center', padding: '30px', color: '#737a84' }}>
                                        No bank account details provided by seller yet.
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* ═══════════════════════════════════════════════════════════════════ */}
                    {/* TAB 10: ACTIVITY AUDIT                                             */}
                    {/* ═══════════════════════════════════════════════════════════════════ */}
                    {activeTab === 'activity' && (
                        <div className="v360-panel">
                            <div className="v360-panel-header">
                                <div>
                                    <h3 className="v360-panel-title">Activity Audit Trail</h3>
                                    <span className="v360-panel-sub">Complete chronological event logs</span>
                                </div>
                            </div>

                            {activityTimeline.length > 0 ? (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                                    {activityTimeline.map((item, idx) => (
                                        <div key={idx} style={{ display: 'flex', alignItems: 'flex-start', gap: '14px', borderBottom: '1px solid #edf0f4', paddingBottom: '12px' }}>
                                            <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: '#a85050', marginTop: '5px' }} />
                                            <div>
                                                <div style={{ fontSize: '14px', fontWeight: 800, color: '#111315' }}>{item.event}</div>
                                                <div style={{ fontSize: '12px', color: '#737a84' }}>
                                                    {new Date(item.date).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div style={{ textAlign: 'center', padding: '40px', color: '#737a84' }}>
                                    No activity records available.
                                </div>
                            )}
                        </div>
                    )}

                    {/* ═══════════════════════════════════════════════════════════════════ */}
                    {/* TAB 11: MESSAGES                                                    */}
                    {/* ═══════════════════════════════════════════════════════════════════ */}
                    {activeTab === 'messages' && (
                        <div className="v360-panel">
                            <div className="v360-panel-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div>
                                    <h3 className="v360-panel-title">Vendor Communication</h3>
                                    <span className="v360-panel-sub">Direct messaging history with {vendor.businessName || 'Seller'}</span>
                                </div>
                                <button
                                    type="button"
                                    className="v360-btn-success"
                                    onClick={() => setMessageModalOpen(true)}
                                    style={{ padding: '6px 14px', fontSize: '12.5px' }}
                                >
                                    ✉ New Message
                                </button>
                            </div>

                            <div style={{ marginTop: '16px' }}>
                                {conversations.length > 0 ? (
                                    <div style={{ display: 'grid', gridTemplateColumns: activeConversation ? '300px 1fr' : '1fr', gap: '16px' }}>
                                        {/* Conversation list */}
                                        <div style={{ border: '1px solid #e2e8f0', borderRadius: '10px', overflow: 'hidden' }}>
                                            {conversations.map((conv) => (
                                                <div
                                                    key={conv._id}
                                                    onClick={() => {
                                                        setActiveConversation(conv);
                                                        fetchConversationMessages(conv._id);
                                                    }}
                                                    style={{
                                                        padding: '12px 14px',
                                                        borderBottom: '1px solid #f1f5f9',
                                                        cursor: 'pointer',
                                                        background: activeConversation?._id === conv._id ? '#f0fdf4' : '#fff',
                                                        transition: 'background 0.15s'
                                                    }}
                                                >
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                        <strong style={{ fontSize: '13px', color: '#0f172a' }}>{conv.vendor?.firstName || 'Vendor'}</strong>
                                                        {conv.adminUnreadCount > 0 && (
                                                            <span style={{ background: '#dc2626', color: '#fff', fontSize: '10.5px', fontWeight: 800, padding: '1px 6px', borderRadius: '99px' }}>
                                                                {conv.adminUnreadCount}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#64748b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                        {conv.lastMessage || 'No messages yet'}
                                                    </p>
                                                    <span style={{ fontSize: '11px', color: '#94a3b8' }}>
                                                        {new Date(conv.lastMessageAt || conv.createdAt).toLocaleDateString('en-IN')}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>

                                        {/* Message thread */}
                                        {activeConversation && (
                                            <div style={{ border: '1px solid #e2e8f0', borderRadius: '10px', display: 'flex', flexDirection: 'column', height: '420px' }}>
                                                <div style={{ flex: 1, overflowY: 'auto', padding: '14px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                                    {conversationMessages.map((msg) => (
                                                        <div
                                                            key={msg._id}
                                                            style={{
                                                                alignSelf: msg.senderRole === 'admin' ? 'flex-end' : 'flex-start',
                                                                maxWidth: '75%',
                                                                background: msg.senderRole === 'admin' ? '#1e40af' : '#f1f5f9',
                                                                color: msg.senderRole === 'admin' ? '#fff' : '#0f172a',
                                                                borderRadius: '12px',
                                                                padding: '10px 14px',
                                                                fontSize: '13px'
                                                            }}
                                                        >
                                                            {msg.subject && <strong style={{ display: 'block', marginBottom: '4px', fontSize: '12px' }}>{msg.subject}</strong>}
                                                            <p style={{ margin: 0 }}>{msg.message}</p>
                                                            <span style={{ fontSize: '10px', opacity: 0.7, display: 'block', marginTop: '4px', textAlign: 'right' }}>
                                                                {new Date(msg.createdAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                                                            </span>
                                                        </div>
                                                    ))}
                                                </div>
                                                <div style={{ borderTop: '1px solid #e2e8f0', padding: '10px', display: 'flex', gap: '8px' }}>
                                                    <input
                                                        type="text"
                                                        value={messageReply}
                                                        onChange={(e) => setMessageReply(e.target.value)}
                                                        onKeyDown={(e) => e.key === 'Enter' && handleSendReply(activeConversation._id)}
                                                        placeholder="Type a reply..."
                                                        style={{ flex: 1, padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '13px', outline: 'none' }}
                                                    />
                                                    <button
                                                        type="button"
                                                        onClick={() => handleSendReply(activeConversation._id)}
                                                        className="btn btn-primary btn-sm"
                                                        style={{ padding: '8px 16px', fontWeight: 700 }}
                                                    >
                                                        Send
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <div style={{ textAlign: 'center', padding: '40px', color: '#737a84' }}>
                                        <p>No message thread with this vendor yet.</p>
                                        <button
                                            type="button"
                                            className="v360-btn-success"
                                            onClick={() => setMessageModalOpen(true)}
                                            style={{ margin: '0 auto' }}
                                        >
                                            ✉ Send First Message
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* ═══════════════════════════════════════════════════════════════════ */}
                    {/* TAB 12: PRODUCT REVIEW REQUESTS                                    */}
                    {/* ═══════════════════════════════════════════════════════════════════ */}
                    {activeTab === 'review-requests' && (
                        <div className="v360-panel">
                            <div className="v360-panel-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div>
                                    <h3 className="v360-panel-title">Product Review Requests</h3>
                                    <span className="v360-panel-sub">Approve or maintain blocks on vendor product listings</span>
                                </div>
                                <button
                                    type="button"
                                    className="v360-btn-secondary"
                                    onClick={fetchReviewRequests}
                                    style={{ padding: '6px 12px', fontSize: '12px' }}
                                >
                                    <RefreshIcon style={{ fontSize: '14px' }} /> Refresh Requests
                                </button>
                            </div>

                            <div style={{ marginTop: '16px' }}>
                                {reviewRequests.length > 0 ? (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                                        {reviewRequests.map((req) => (
                                            <div
                                                key={req._id}
                                                style={{
                                                    background: '#fff',
                                                    border: '1px solid #e2e8f0',
                                                    borderRadius: '12px',
                                                    padding: '16px 20px',
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    flexWrap: 'wrap',
                                                    gap: '14px'
                                                }}
                                            >
                                                <div>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                        <strong style={{ fontSize: '14px', color: '#0f172a' }}>{req.product?.name || 'Product'}</strong>
                                                        <span style={{
                                                            fontSize: '11px',
                                                            fontWeight: 800,
                                                            padding: '2px 8px',
                                                            borderRadius: '99px',
                                                            background: req.status === 'approved' ? '#dcfce7' : req.status === 'rejected' ? '#fee2e2' : '#fef3c7',
                                                            color: req.status === 'approved' ? '#166534' : req.status === 'rejected' ? '#991b1b' : '#92400e'
                                                        }}>
                                                            ● {req.status?.toUpperCase()}
                                                        </span>
                                                    </div>
                                                    <p style={{ margin: '6px 0 2px', fontSize: '13px', color: '#334155' }}>
                                                        <strong>Vendor Note:</strong> {req.vendorMessage}
                                                    </p>
                                                    <span style={{ fontSize: '11.5px', color: '#94a3b8' }}>
                                                        Submitted on {new Date(req.createdAt).toLocaleDateString('en-IN', { dateStyle: 'medium' })}
                                                    </span>
                                                </div>

                                                {req.status === 'pending' && (
                                                    <div style={{ display: 'flex', gap: '8px' }}>
                                                        <button
                                                            type="button"
                                                            className="v360-btn-success"
                                                            onClick={() => handleResolveReview(req._id, 'approved', 'Listing approved by administrator.')}
                                                            disabled={actionLoading}
                                                            style={{ padding: '6px 14px', fontSize: '12px' }}
                                                        >
                                                            <CheckIcon style={{ fontSize: '14px' }} /> Approve Listing
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="v360-btn-danger"
                                                            onClick={() => {
                                                                const note = prompt("Enter reason to keep product blocked:") || "Product does not meet marketplace standards.";
                                                                handleResolveReview(req._id, 'rejected', note);
                                                            }}
                                                            disabled={actionLoading}
                                                            style={{ padding: '6px 14px', fontSize: '12px' }}
                                                        >
                                                            <CloseIcon style={{ fontSize: '14px' }} /> Keep Blocked
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div style={{ textAlign: 'center', padding: '40px', color: '#737a84' }}>
                                        No product review requests pending.
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </main>
            </div>

            {/* ═════════════════════════════════════════════════════════════════════════════ */}
            {/* 1. BANK ACCOUNT REVIEW MODAL                                                 */}
            {/* ═════════════════════════════════════════════════════════════════════════════ */}
            {bankModalOpen && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.65)', backdropFilter: 'blur(4px)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
                    <div style={{ background: '#ffffff', borderRadius: '16px', maxWidth: '620px', width: '100%', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)', border: '1px solid #e2e8f0', padding: '26px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', borderBottom: '1px solid #f1f5f9', paddingBottom: '16px', marginBottom: '18px' }}>
                            <div>
                                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 900, color: '#0f172a' }}>Bank Account Verification &amp; Payout Review</h3>
                                <p style={{ margin: '4px 0 0', fontSize: '12.5px', color: '#64748b' }}>
                                    Settlement destination for {vendor.businessName || 'Seller'}
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={() => setBankModalOpen(false)}
                                style={{ background: '#f1f5f9', border: 'none', borderRadius: '8px', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#475569', fontWeight: 700 }}
                            >
                                ✕
                            </button>
                        </div>

                        {vendor.bankDetails && (vendor.bankDetails.accountNumberMasked || vendor.bankDetails.accountHolderName || vendor.bankDetails.bankName || vendor.bankDetails.accountNumber) ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                                {/* Status Banner */}
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    padding: '12px 16px',
                                    borderRadius: '10px',
                                    background: vendor.bankDetails.verificationStatus === 'verified' ? '#f0fdf4' : vendor.bankDetails.verificationStatus === 'rejected' ? '#fef2f2' : '#fffbeb',
                                    border: `1px solid ${vendor.bankDetails.verificationStatus === 'verified' ? '#bbf7d0' : vendor.bankDetails.verificationStatus === 'rejected' ? '#fecaca' : '#fde68a'}`
                                }}>
                                    <div>
                                        <span style={{ fontSize: '11px', fontWeight: 800, textTransform: 'uppercase', color: '#64748b' }}>Verification State</span>
                                        <div style={{
                                            fontSize: '14px',
                                            fontWeight: 800,
                                            color: vendor.bankDetails.verificationStatus === 'verified' ? '#166534' : vendor.bankDetails.verificationStatus === 'rejected' ? '#991b1b' : '#92400e'
                                        }}>
                                            {vendor.bankDetails.verificationStatus === 'verified' ? '✓ Verified & Active for Settlements' : vendor.bankDetails.verificationStatus === 'under_review' ? '● Under Admin Review' : vendor.bankDetails.verificationStatus === 'rejected' ? '✕ Rejected' : 'Pending Verification'}
                                        </div>
                                    </div>
                                    <span style={{
                                        fontSize: '11px',
                                        fontWeight: 800,
                                        padding: '4px 10px',
                                        borderRadius: '99px',
                                        background: vendor.bankDetails.verificationStatus === 'verified' ? '#dcfce7' : '#fef3c7',
                                        color: vendor.bankDetails.verificationStatus === 'verified' ? '#166534' : '#92400e'
                                    }}>
                                        {vendor.bankDetails.accountType ? vendor.bankDetails.accountType.toUpperCase() : 'CURRENT'} ACCOUNT
                                    </span>
                                </div>

                                {/* Bank Account Particulars */}
                                <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '16px' }}>
                                    <div style={{ fontSize: '12px', fontWeight: 800, color: '#334155', textTransform: 'uppercase', marginBottom: '12px' }}>
                                        🏦 Submitted Account Details
                                    </div>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', fontSize: '13px' }}>
                                        <div>
                                            <span style={{ color: '#64748b', fontSize: '11.5px', display: 'block' }}>Account Holder</span>
                                            <strong style={{ color: '#0f172a' }}>{vendor.bankDetails.accountHolderName}</strong>
                                        </div>
                                        <div>
                                            <span style={{ color: '#64748b', fontSize: '11.5px', display: 'block' }}>Bank Name</span>
                                            <strong style={{ color: '#0f172a' }}>{vendor.bankDetails.bankName}</strong>
                                        </div>
                                        <div>
                                            <span style={{ color: '#64748b', fontSize: '11.5px', display: 'block' }}>Account Number</span>
                                            <span style={{ fontFamily: 'monospace', fontWeight: 700, color: '#0f172a' }}>
                                                {vendor.bankDetails.accountNumberMasked}
                                            </span>
                                        </div>
                                        <div>
                                            <span style={{ color: '#64748b', fontSize: '11.5px', display: 'block' }}>IFSC Code</span>
                                            <span style={{ fontFamily: 'monospace', fontWeight: 700, color: '#0f172a' }}>
                                                {vendor.bankDetails.ifsc}
                                            </span>
                                        </div>
                                        {vendor.bankDetails.branchName && (
                                            <div>
                                                <span style={{ color: '#64748b', fontSize: '11.5px', display: 'block' }}>Branch</span>
                                                <span style={{ color: '#0f172a' }}>{vendor.bankDetails.branchName}</span>
                                            </div>
                                        )}
                                        {vendor.bankDetails.upiId && (
                                            <div>
                                                <span style={{ color: '#64748b', fontSize: '11.5px', display: 'block' }}>UPI ID</span>
                                                <span style={{ color: '#0f172a' }}>{vendor.bankDetails.upiId}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Verification Provider Signals */}
                                <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '16px' }}>
                                    <div style={{ fontSize: '12px', fontWeight: 800, color: '#334155', textTransform: 'uppercase', marginBottom: '12px' }}>
                                        🛡️ Verification Engine &amp; Security Signals
                                    </div>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', fontSize: '13px' }}>
                                        <div>
                                            <span style={{ color: '#64748b', fontSize: '11.5px', display: 'block' }}>Verification Provider</span>
                                            <strong style={{ color: '#0f172a' }}>
                                                {vendor.bankDetails.verificationProvider ? vendor.bankDetails.verificationProvider.toUpperCase() : 'CASHFREE SECUREID'}
                                            </strong>
                                        </div>
                                        <div>
                                            <span style={{ color: '#64748b', fontSize: '11.5px', display: 'block' }}>Registered Name at Bank</span>
                                            <strong style={{ color: '#0f172a' }}>{vendor.bankDetails.nameAtBank || vendor.bankDetails.accountHolderName}</strong>
                                        </div>
                                        <div>
                                            <span style={{ color: '#64748b', fontSize: '11.5px', display: 'block' }}>Name Match Status</span>
                                            <span style={{
                                                display: 'inline-block',
                                                padding: '2px 8px',
                                                borderRadius: '4px',
                                                fontSize: '11.5px',
                                                fontWeight: 700,
                                                background: vendor.bankDetails.nameMatchStatus === 'MISMATCH' ? '#fee2e2' : '#dcfce7',
                                                color: vendor.bankDetails.nameMatchStatus === 'MISMATCH' ? '#991b1b' : '#166534'
                                            }}>
                                                ✓ {vendor.bankDetails.nameMatchStatus || 'MATCH'} ({vendor.bankDetails.nameMatchScore || 100}%)
                                            </span>
                                        </div>
                                        <div>
                                            <span style={{ color: '#64748b', fontSize: '11.5px', display: 'block' }}>Submission Date</span>
                                            <span style={{ color: '#0f172a' }}>
                                                {new Date(vendor.bankDetails.verifiedAt || vendor.joinedAt || Date.now()).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                {vendor.bankDetails.rejectionReason && (
                                    <div style={{ background: '#fef2f2', border: '1.5px solid #fecaca', borderRadius: '10px', padding: '14px', fontSize: '13px', color: '#991b1b' }}>
                                        <strong>✕ Rejection Reason:</strong> {vendor.bankDetails.rejectionReason}
                                    </div>
                                )}

                                {/* Modal Actions */}
                                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '10px', borderTop: '1px solid #f1f5f9', paddingTop: '16px' }}>
                                    <button
                                        type="button"
                                        onClick={() => setBankModalOpen(false)}
                                        className="btn btn-secondary"
                                        style={{ padding: '8px 18px', fontSize: '13px', borderRadius: '8px' }}
                                    >
                                        Close
                                    </button>

                                    {vendor.bankDetails.verificationStatus !== 'rejected' && (
                                        <button
                                            type="button"
                                            onClick={() => setRejectModalOpen(true)}
                                            className="btn"
                                            style={{ padding: '8px 18px', fontSize: '13px', background: '#fee2e2', color: '#991b1b', border: '1px solid #fecaca', borderRadius: '8px', fontWeight: 700, cursor: 'pointer' }}
                                        >
                                            ✕ Reject Bank Details
                                        </button>
                                    )}

                                    {vendor.bankDetails.verificationStatus !== 'verified' && (
                                        <button
                                            type="button"
                                            onClick={() => setApproveConfirmOpen(true)}
                                            className="btn btn-primary"
                                            style={{ padding: '8px 20px', fontSize: '13px', background: '#16a34a', borderColor: '#16a34a', borderRadius: '8px', fontWeight: 700, cursor: 'pointer' }}
                                        >
                                            ✓ Approve Bank Account
                                        </button>
                                    )}
                                </div>
                            </div>
                        ) : (
                            <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
                                No bank account details have been submitted by this vendor.
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* ═════════════════════════════════════════════════════════════════════════════ */}
            {/* 2. APPROVE CONFIRMATION MODAL                                                */}
            {/* ═════════════════════════════════════════════════════════════════════════════ */}
            {approveConfirmOpen && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.75)', backdropFilter: 'blur(4px)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
                    <div style={{ background: '#ffffff', borderRadius: '16px', maxWidth: '500px', width: '100%', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)', border: '1px solid #e2e8f0', padding: '26px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '14px' }}>
                            <div style={{ width: '42px', height: '42px', borderRadius: '50%', background: '#dcfce7', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#16a34a', fontSize: '20px', fontWeight: 900 }}>
                                ✓
                            </div>
                            <div>
                                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 900, color: '#0f172a' }}>Approve Bank Account?</h3>
                                <span style={{ fontSize: '12.5px', color: '#64748b' }}>Activate official payout settlement destination</span>
                            </div>
                        </div>

                        <p style={{ fontSize: '13.5px', color: '#334155', margin: '0 0 16px', lineHeight: '1.5' }}>
                            You are approving this verified settlement account for:
                        </p>

                        <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '14px 16px', marginBottom: '16px', fontSize: '13px' }}>
                            <div style={{ marginBottom: '8px' }}>
                                <span style={{ color: '#64748b', fontSize: '11.5px', display: 'block' }}>Vendor Store:</span>
                                <strong style={{ color: '#0f172a' }}>{vendor.businessName || vendor.sellerName}</strong>
                            </div>
                            <div style={{ marginBottom: '8px' }}>
                                <span style={{ color: '#64748b', fontSize: '11.5px', display: 'block' }}>Bank:</span>
                                <strong style={{ color: '#0f172a' }}>{vendor.bankDetails?.bankName}</strong>
                            </div>
                            <div style={{ marginBottom: '8px' }}>
                                <span style={{ color: '#64748b', fontSize: '11.5px', display: 'block' }}>Masked Account Number:</span>
                                <span style={{ fontFamily: 'monospace', fontWeight: 700, color: '#0f172a' }}>
                                    {vendor.bankDetails?.accountNumberMasked}
                                </span>
                            </div>
                            <div>
                                <span style={{ color: '#64748b', fontSize: '11.5px', display: 'block' }}>IFSC Code:</span>
                                <span style={{ fontFamily: 'monospace', fontWeight: 700, color: '#0f172a' }}>
                                    {vendor.bankDetails?.ifsc}
                                </span>
                            </div>
                        </div>

                        <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '8px', padding: '12px', fontSize: '12px', color: '#1e40af', marginBottom: '20px' }}>
                            ℹ️ <strong>Settlement Activation:</strong> Marketplace sales revenue will be settled into this account. If all other compliance gates (Application &amp; Documents) are verified, product listing will automatically unlock.
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                            <button
                                type="button"
                                onClick={() => setApproveConfirmOpen(false)}
                                disabled={bankActionLoading}
                                className="btn btn-secondary"
                                style={{ padding: '8px 18px', fontSize: '13px', borderRadius: '8px' }}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={handleApproveBank}
                                disabled={bankActionLoading}
                                className="btn btn-primary"
                                style={{ padding: '8px 22px', fontSize: '13px', background: '#16a34a', borderColor: '#16a34a', borderRadius: '8px', fontWeight: 700, cursor: 'pointer' }}
                            >
                                {bankActionLoading ? 'Approving...' : 'Confirm Approval'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ═════════════════════════════════════════════════════════════════════════════ */}
            {/* 3. REJECT BANK ACCOUNT MODAL                                                 */}
            {/* ═════════════════════════════════════════════════════════════════════════════ */}
            {rejectModalOpen && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.75)', backdropFilter: 'blur(4px)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
                    <form onSubmit={handleRejectBank} style={{ background: '#ffffff', borderRadius: '16px', maxWidth: '500px', width: '100%', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)', border: '1px solid #e2e8f0', padding: '26px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '14px' }}>
                            <div style={{ width: '42px', height: '42px', borderRadius: '50%', background: '#fee2e2', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#dc2626', fontSize: '20px', fontWeight: 900 }}>
                                ✕
                            </div>
                            <div>
                                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 900, color: '#0f172a' }}>Reject Bank Account</h3>
                                <span style={{ fontSize: '12.5px', color: '#64748b' }}>Provide specific feedback so the vendor can resubmit</span>
                            </div>
                        </div>

                        <div style={{ marginBottom: '16px' }}>
                            <label style={{ display: 'block', fontSize: '12.5px', fontWeight: 800, color: '#334155', marginBottom: '8px' }}>
                                Reason Category *
                            </label>
                            <select
                                value={rejectReasonType}
                                onChange={(e) => setRejectReasonType(e.target.value)}
                                style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1.5px solid #cbd5e1', fontSize: '13.5px', color: '#0f172a', outline: 'none' }}
                            >
                                <option value="Bank details don't match">Bank details don't match</option>
                                <option value="Account verification failed">Account verification failed</option>
                                <option value="Name mismatch">Name mismatch</option>
                                <option value="Invalid bank information">Invalid bank information / IFSC</option>
                                <option value="Documents/information insufficient">Documents/information insufficient</option>
                                <option value="Other">Other (Custom explanation)</option>
                            </select>
                        </div>

                        <div style={{ marginBottom: '16px' }}>
                            <label style={{ display: 'block', fontSize: '12.5px', fontWeight: 800, color: '#334155', marginBottom: '8px' }}>
                                Additional Details / Correction Notes *
                            </label>
                            <textarea
                                rows={3}
                                required
                                value={customRejectReason}
                                onChange={(e) => setCustomRejectReason(e.target.value)}
                                placeholder="Explain what needs correction (e.g., account holder name must match PAN identity)..."
                                style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1.5px solid #cbd5e1', fontSize: '13px', outline: 'none', resize: 'vertical' }}
                            />
                        </div>

                        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', padding: '10px 12px', fontSize: '12px', color: '#991b1b', marginBottom: '20px' }}>
                            ⚠️ The seller will receive this explanation in their Seller Center and will be asked to correct and resubmit their details.
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                            <button
                                type="button"
                                onClick={() => setRejectModalOpen(false)}
                                disabled={bankActionLoading}
                                className="btn btn-secondary"
                                style={{ padding: '8px 18px', fontSize: '13px', borderRadius: '8px' }}
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={bankActionLoading}
                                className="btn btn-danger"
                                style={{ padding: '8px 22px', fontSize: '13px', background: '#dc2626', borderColor: '#dc2626', borderRadius: '8px', fontWeight: 700, cursor: 'pointer' }}
                            >
                                {bankActionLoading ? 'Rejecting...' : 'Reject Bank Account'}
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {/* ═════════════════════════════════════════════════════════════════════════════ */}
            {/* 4. BLOCK VENDOR MODAL                                                         */}
            {/* ═════════════════════════════════════════════════════════════════════════════ */}
            {blockVendorOpen && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.65)', backdropFilter: 'blur(4px)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
                    <div style={{ background: '#ffffff', borderRadius: '16px', maxWidth: '520px', width: '100%', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)', border: '1px solid #e2e8f0', padding: '26px' }}>
                        <div style={{ borderBottom: '1px solid #f1f5f9', paddingBottom: '14px', marginBottom: '16px' }}>
                            <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 900, color: '#dc2626', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                ⛔ Block Vendor Account
                            </h3>
                            <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#64748b' }}>
                                Vendor: <strong>{vendor.businessName || 'Seller'}</strong>
                            </p>
                        </div>

                        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', padding: '12px 14px', fontSize: '12.5px', color: '#991b1b', marginBottom: '16px' }}>
                            <strong>Blocking this vendor will:</strong>
                            <ul style={{ margin: '6px 0 0', paddingLeft: '18px' }}>
                                <li>Disable all marketplace activity</li>
                                <li>Immediately hide all their products from customers across Web, Mobile &amp; Search</li>
                                <li>Prevent publishing or modifying listings</li>
                            </ul>
                        </div>

                        <form onSubmit={handleBlockVendor}>
                            <div style={{ marginBottom: '16px' }}>
                                <label style={{ display: 'block', fontSize: '12.5px', fontWeight: 800, color: '#334155', marginBottom: '6px' }}>
                                    Reason for Blocking *
                                </label>
                                <textarea
                                    rows={3}
                                    required
                                    value={blockReason}
                                    onChange={(e) => setBlockReason(e.target.value)}
                                    placeholder="Enter authoritative compliance or policy violation reason..."
                                    style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1.5px solid #cbd5e1', fontSize: '13px', outline: 'none' }}
                                />
                            </div>

                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                                <button
                                    type="button"
                                    onClick={() => setBlockVendorOpen(false)}
                                    disabled={actionLoading}
                                    className="btn btn-secondary"
                                    style={{ padding: '8px 18px', fontSize: '13px' }}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={actionLoading}
                                    className="btn btn-danger"
                                    style={{ padding: '8px 22px', fontSize: '13px', background: '#dc2626', fontWeight: 700 }}
                                >
                                    {actionLoading ? 'Blocking...' : '⛔ Block Vendor'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* ═════════════════════════════════════════════════════════════════════════════ */}
            {/* 5. UNBLOCK VENDOR MODAL                                                       */}
            {/* ═════════════════════════════════════════════════════════════════════════════ */}
            {unblockConfirmOpen && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.65)', backdropFilter: 'blur(4px)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
                    <div style={{ background: '#ffffff', borderRadius: '16px', maxWidth: '480px', width: '100%', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)', border: '1px solid #e2e8f0', padding: '26px' }}>
                        <h3 style={{ margin: '0 0 10px', fontSize: '18px', fontWeight: 900, color: '#0f172a' }}>
                            Unblock Vendor?
                        </h3>
                        <p style={{ fontSize: '13px', color: '#475569', lineHeight: 1.6 }}>
                            This will restore <strong>{vendor.businessName || 'this vendor'}</strong>'s ability to operate on the marketplace.
                            Product visibility will be subject to their listing permission and individual product approval statuses.
                        </p>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px' }}>
                            <button
                                type="button"
                                onClick={() => setUnblockConfirmOpen(false)}
                                disabled={actionLoading}
                                className="btn btn-secondary"
                                style={{ padding: '8px 18px', fontSize: '13px' }}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={handleUnblockVendor}
                                disabled={actionLoading}
                                className="btn btn-success"
                                style={{ padding: '8px 22px', fontSize: '13px', fontWeight: 700 }}
                            >
                                {actionLoading ? 'Unblocking...' : '✓ Unblock Vendor'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ═════════════════════════════════════════════════════════════════════════════ */}
            {/* 6. BLOCK PRODUCT MODAL                                                        */}
            {/* ═════════════════════════════════════════════════════════════════════════════ */}
            {productBlockOpen && productBlockTarget && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.65)', backdropFilter: 'blur(4px)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
                    <div style={{ background: '#ffffff', borderRadius: '16px', maxWidth: '500px', width: '100%', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)', border: '1px solid #e2e8f0', padding: '26px' }}>
                        <div style={{ borderBottom: '1px solid #f1f5f9', paddingBottom: '14px', marginBottom: '16px' }}>
                            <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 900, color: '#dc2626' }}>
                                ⛔ Block Product Listing
                            </h3>
                            <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#64748b' }}>
                                Product: <strong>{productBlockTarget.name}</strong>
                            </p>
                        </div>

                        <form onSubmit={handleBlockProduct}>
                            <div style={{ marginBottom: '16px' }}>
                                <label style={{ display: 'block', fontSize: '12.5px', fontWeight: 800, color: '#334155', marginBottom: '6px' }}>
                                    Reason for Blocking *
                                </label>
                                <textarea
                                    rows={3}
                                    required
                                    value={productBlockReason}
                                    onChange={(e) => setProductBlockReason(e.target.value)}
                                    placeholder="Explain why this listing is being blocked (e.g., prohibited items, copyright violation)..."
                                    style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1.5px solid #cbd5e1', fontSize: '13px', outline: 'none' }}
                                />
                            </div>

                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setProductBlockOpen(false);
                                        setProductBlockTarget(null);
                                    }}
                                    disabled={actionLoading}
                                    className="btn btn-secondary"
                                    style={{ padding: '8px 18px', fontSize: '13px' }}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={actionLoading}
                                    className="btn btn-danger"
                                    style={{ padding: '8px 22px', fontSize: '13px', background: '#dc2626', fontWeight: 700 }}
                                >
                                    {actionLoading ? 'Blocking...' : 'Block Product'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* ═════════════════════════════════════════════════════════════════════════════ */}
            {/* 7. MESSAGE VENDOR MODAL                                                       */}
            {/* ═════════════════════════════════════════════════════════════════════════════ */}
            {messageModalOpen && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.65)', backdropFilter: 'blur(4px)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
                    <div style={{ background: '#ffffff', borderRadius: '16px', maxWidth: '520px', width: '100%', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)', border: '1px solid #e2e8f0', padding: '26px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #f1f5f9', paddingBottom: '14px', marginBottom: '16px' }}>
                            <div>
                                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 900, color: '#0f172a' }}>
                                    ✉ Send Message to Vendor
                                </h3>
                                <p style={{ margin: '2px 0 0', fontSize: '12.5px', color: '#64748b' }}>
                                    To: <strong>{vendor.businessName || 'Seller'}</strong> ({vendor.sellerName})
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={() => setMessageModalOpen(false)}
                                style={{ background: '#f1f5f9', border: 'none', borderRadius: '8px', width: '32px', height: '32px', cursor: 'pointer', fontWeight: 700 }}
                            >
                                ✕
                            </button>
                        </div>

                        <form onSubmit={handleSendMessage}>
                            <div style={{ marginBottom: '14px' }}>
                                <label style={{ display: 'block', fontSize: '12.5px', fontWeight: 800, color: '#334155', marginBottom: '6px' }}>
                                    Subject
                                </label>
                                <input
                                    type="text"
                                    value={messageSubject}
                                    onChange={(e) => setMessageSubject(e.target.value)}
                                    placeholder="e.g., Bank Verification Update / Product Listing Correction"
                                    style={{ width: '100%', padding: '8px 12px', borderRadius: '8px', border: '1.5px solid #cbd5e1', fontSize: '13px', outline: 'none' }}
                                />
                            </div>

                            <div style={{ marginBottom: '16px' }}>
                                <label style={{ display: 'block', fontSize: '12.5px', fontWeight: 800, color: '#334155', marginBottom: '6px' }}>
                                    Message *
                                </label>
                                <textarea
                                    rows={4}
                                    required
                                    value={messageBody}
                                    onChange={(e) => setMessageBody(e.target.value)}
                                    placeholder="Type your message to the vendor..."
                                    style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1.5px solid #cbd5e1', fontSize: '13px', outline: 'none' }}
                                />
                            </div>

                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                                <button
                                    type="button"
                                    onClick={() => setMessageModalOpen(false)}
                                    disabled={actionLoading}
                                    className="btn btn-secondary"
                                    style={{ padding: '8px 18px', fontSize: '13px' }}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={actionLoading}
                                    className="btn btn-primary"
                                    style={{ padding: '8px 22px', fontSize: '13px', fontWeight: 700 }}
                                >
                                    {actionLoading ? 'Sending...' : 'Send Message'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </Fragment>
    );
}

export default Vendor360;
